import React, { useState, useEffect, useCallback, useRef } from 'react';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import SmartAmountInput from '../../../shared/components/SmartAmountInput';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import SuccessPopup from '../../../shared/components/SuccessPopup';
import WarningPopup from '../../../shared/components/WarningPopup';
import Step1ControlReceipt from './Step1ControlReceipt';
import Step2GeneralJournal from './Step2GeneralJournal';
import Step3VatReport from './Step3VatReport';
import Step4TrialBalance from './Step4TrialBalance';
import Step5Result from './Step5Result';
import Step6FinalStatus from './Step6FinalStatus';
import {
  AccountPeriodCloseRecord,
  CustomerOption,
  TrialBalancePeriodRecord,
  TrialBalanceYearRecord,
  AccountPeriodCloseItemRecord,
  ControlReceiptRecord,
  NoLedgerTransactionRecord,
  ControlVatReportRecord,
  JournalRecord,
  AccountOption,
  CashflowCategoryOption,
  NewCloseData,
  JournalFormData
} from './types';
import { formatNumber, formatDate, resolvePeriodTypeForApi, DEFAULT_YEAR_TYPE, buildAccountPeriodCloseQueryParams, getAccountPeriodClosePeriodType, getPeriodEndPostDate } from './utils';


const AccountPeriodClose: React.FC = () => {
  const { userAuth } = useAuth();
  const [closeData, setCloseData] = useState<AccountPeriodCloseRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Debug: Log when warning alert state changes
  useEffect(() => {
    if (showWarningAlert) {
      console.log('🔔 WarningPopup state changed to visible:', {
        showWarningAlert,
        warningMessage,
        timestamp: new Date().toISOString()
      });
    }
  }, [showWarningAlert, warningMessage]);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showUndoConfirmPopup, setShowUndoConfirmPopup] = useState(false);
  const [undoAccountPeriodCloseOid, setUndoAccountPeriodCloseOid] = useState<string | null>(null);
  
  // Filter state
  const [selectedCustomerOid, setSelectedCustomerOid] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  
  // Customer list state
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  // Add popup state
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [newCloseData, setNewCloseData] = useState<NewCloseData>({
    CustomerOid: '',
    YearType: DEFAULT_YEAR_TYPE,
    PeriodType: '2',
    IsManual: '0',
    ManualItemCostAmount: '0',
    ClearOld: '1',
    UseRandom: '1'
  });
  
  // Trial Balance state
  const [trialBalanceData, setTrialBalanceData] = useState<TrialBalancePeriodRecord[]>([]);
  const [loadingTrialBalance, setLoadingTrialBalance] = useState(false);
  const [showTrialBalance, setShowTrialBalance] = useState(false);
  
  // Control Receipt state
  const [controlReceiptData, setControlReceiptData] = useState<ControlReceiptRecord[]>([]);
  const [loadingControlReceipt, setLoadingControlReceipt] = useState(false);
  const [syncingReceipts, setSyncingReceipts] = useState(false);
  
  // General Journal (No Ledger Transaction List) state
  const [noLedgerTransactionData, setNoLedgerTransactionData] = useState<NoLedgerTransactionRecord[]>([]);
  const [loadingNoLedgerTransaction, setLoadingNoLedgerTransaction] = useState(false);
  const [rebuildingLedger, setRebuildingLedger] = useState(false);
  const [ledgerRebuildError, setLedgerRebuildError] = useState<string | null>(null);
  const isRebuildingRef = useRef(false);
  const lastRebuildTimeRef = useRef<number>(0);
  const rebuildAttemptedRef = useRef(false); // Track if rebuild has been attempted
  const rebuildRequestIdRef = useRef<string | null>(null); // Track current request ID to prevent duplicates
  const activeRebuildPromiseRef = useRef<Promise<any> | null>(null); // Track active promise to prevent duplicates
  const step6ApisCalledRef = useRef(false);
  
  // VAT Control Report state
  const [vatReportData, setVatReportData] = useState<ControlVatReportRecord[]>([]);
  const [vatReportSummary, setVatReportSummary] = useState<ControlVatReportRecord | null>(null);
  const [loadingVatReport, setLoadingVatReport] = useState(false);
  
  // Journal Entries state
  const [journalData, setJournalData] = useState<JournalRecord[]>([]);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [showJournalEntries, setShowJournalEntries] = useState(false);
  const [showJournalPopup, setShowJournalPopup] = useState(false);
  const [editingJournal, setEditingJournal] = useState<JournalRecord | null>(null);
  const [isPreviousYear, setIsPreviousYear] = useState(false);
  const [isWithVat, setIsWithVat] = useState(false); // State for НӨАТ-тэй checkbox
  const [journalFormData, setJournalFormData] = useState<JournalFormData>({
    Date: '',
    Description: '',
    AccountId: '',
    OffsetAccountId: '',
    AmountDebit: '',
    AmountCredit: '',
    VatAmount: '',
    Amount: '',
    CashflowCategoryOid: '',
    YearType: '',
    PeriodType: '',
    DayType: ''
  });
  
  // Accounts state
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [cashflowCategories, setCashflowCategories] = useState<CashflowCategoryOption[]>([]);
  const [loadingCashflowCategories, setLoadingCashflowCategories] = useState(false);
  
  // Tab/Step state
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1); // 1: Control Receipt, 2: General Journal, 3: VAT Control Report, 4: Trial Balance, 5: AccountPeriodClose Result, 6: Trial Balance Year
  const [accountPeriodCloseResult, setAccountPeriodCloseResult] = useState<any>(null);
  const [accountPeriodCloseItems, setAccountPeriodCloseItems] = useState<AccountPeriodCloseItemRecord[]>([]);
  const [batch4Amount, setBatch4Amount] = useState<number | null>(null); // Batch 4 дүн уруулах
  const [costOfGoodsSold, setCostOfGoodsSold] = useState<number | null>(null); // Борлуулсан бүтээгдэхүүний өртөг

  // Fetch customers list for dropdown
  const fetchCustomers = useCallback(async () => {
    try {
      const accountantOid = userAuth.accountantOid || localStorage.getItem('accountantOid');
      
      if (!accountantOid) {
        return;
      }

      const queryParams = new URLSearchParams({
        accountantOid: accountantOid
      });
      const apiUrl = `/api/customerslist?${queryParams.toString()}`;
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        const customerOptions: CustomerOption[] = result.data.map((customer: any) => ({
          Oid: customer.Oid,
          CustomerID: customer.CustomerID,
          CustomerName: customer.CustomerName
        }));
        setCustomers(customerOptions);
      }
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
    }
  }, [userAuth.accountantOid]);

  // Fetch AccountPeriodClose data
  const fetchAccountPeriodClose = useCallback(async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams();
      
      if (selectedCustomerOid) {
        queryParams.append('CustomerOid', selectedCustomerOid);
      }
      
      if (selectedYear) {
        queryParams.append('Year', selectedYear);
      }
      
      const apiUrl = `/api/account-period-close${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      console.log('🌐 API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched account period close data:`, result.data);
        
        // Handle different response structures
        let items: AccountPeriodCloseItemRecord[] = [];
        if (result.data.recordset && Array.isArray(result.data.recordset)) {
          items = result.data.recordset;
        } else if (result.data.data && result.data.data.recordset && Array.isArray(result.data.data.recordset)) {
          items = result.data.data.recordset;
        } else if (Array.isArray(result.data)) {
          items = result.data;
        }
        
        if (items.length > 0) {
          setAccountPeriodCloseItems(items);
          setAccountPeriodCloseResult(result);
          
          // Find item with BatchNumber === 4 and AccountCode === "140100" to get WT_CreditAmount
          console.log('🔍 Searching for BatchNumber=4, AccountCode=140100 in items:', items.length);
          const targetItem = items.find((item: AccountPeriodCloseItemRecord) => {
            const matches = item.BatchNumber === 4 && item.AccountCode === "140100";
            if (matches) {
              console.log('✅ Found target item:', {
                BatchNumber: item.BatchNumber,
                AccountCode: item.AccountCode,
                AccountName: item.AccountName,
                WT_CreditAmount: item.WT_CreditAmount,
                Description: item.Description
              });
            }
            return matches;
          });
          
          if (targetItem && (targetItem.WT_CreditAmount !== null && targetItem.WT_CreditAmount !== undefined)) {
            setCostOfGoodsSold(targetItem.WT_CreditAmount);
            console.log('✅ Set default costOfGoodsSold from WT_CreditAmount:', targetItem.WT_CreditAmount);
          } else {
            console.log('⚠️ Target item not found, trying fallback...');
            // Fallback: try to find by description
            const fallbackItem = items.find((item: AccountPeriodCloseItemRecord) => 
              item.BatchNumber === 4 && 
              (item.Description?.includes('Борлуулсан бүтээгдэхүүний өртөг') || 
               item.Description?.includes('Inventory') ||
               item.Description?.includes('Бараа материал'))
            );
            
            if (fallbackItem && (fallbackItem.WT_CreditAmount !== null && fallbackItem.WT_CreditAmount !== undefined)) {
              setCostOfGoodsSold(fallbackItem.WT_CreditAmount);
              console.log('✅ Set default costOfGoodsSold from fallback WT_CreditAmount:', fallbackItem.WT_CreditAmount);
            } else {
              // Last fallback: first item in Batch 4 with WT_CreditAmount
              const batch4Items = items.filter((item: AccountPeriodCloseItemRecord) => item.BatchNumber === 4);
              console.log('🔍 Batch 4 items found:', batch4Items.length);
              if (batch4Items.length > 0) {
                const firstBatch4Item = batch4Items.find(item => item.WT_CreditAmount && item.WT_CreditAmount > 0) || batch4Items[0];
                if (firstBatch4Item && (firstBatch4Item.WT_CreditAmount !== null && firstBatch4Item.WT_CreditAmount !== undefined)) {
                  setCostOfGoodsSold(firstBatch4Item.WT_CreditAmount);
                  console.log('✅ Set default costOfGoodsSold from first Batch 4 item WT_CreditAmount:', firstBatch4Item.WT_CreditAmount);
                } else {
                  console.log('⚠️ No WT_CreditAmount found in Batch 4 items');
                }
              }
            }
          }
        }
        
        setCloseData(result.data);
        
        // Show warning if no data found
        const dataLength = Array.isArray(result.data) ? result.data.length : (items.length || 0);
        if (dataLength === 0) {
          setWarningMessage('Хаалтын гүйлгээний мэдээлэл олдсонгүй');
          setShowWarningAlert(true);
        } else {
          setShowWarningAlert(false);
        }
      } else {
        setErrorMessage('Хаалтын гүйлгээний мэдээлэл авахад алдаа гарлаа');
        setShowErrorPopup(true);
        setCloseData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching AccountPeriodClose:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'Хаалтын гүйлгээний мэдээлэл авахад алдаа гарлаа';
      
      if (error.response?.status === 404) {
        errorMessage = 'API endpoint олдсонгүй. Сервертэй холбогдож чадсангүй.';
        setWarningMessage('Хаалтын гүйлгээний мэдээлэл олдсонгүй');
        setShowWarningAlert(true);
      } else if (error.response?.status === 401) {
        errorMessage = 'Нэвтрэх эрх шаардлагатай. Дахин нэвтэрнэ үү.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Серверийн алдаа. Дахин оролдоно уу.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = 'Сүлжээний алдаа. Сервер ажиллаж байгаа эсэхийг шалгана уу.';
      }
      
      setErrorMessage(errorMessage);
      setShowErrorPopup(true);
      setCloseData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerOid, selectedYear]);

  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    fetchAccountPeriodClose();
  };

  const handleAdd = () => {
    setNewCloseData({
      CustomerOid: selectedCustomerOid || '',
      YearType: DEFAULT_YEAR_TYPE,
      PeriodType: '2',
      IsManual: '0',
      ManualItemCostAmount: '0',
      ClearOld: '1',
      UseRandom: '1'
    });
    setShowAddPopup(true);
  };

  // Handle Undo button click
  const handleUndoClick = (accountPeriodCloseOid: string) => {
    setUndoAccountPeriodCloseOid(accountPeriodCloseOid);
    setShowUndoConfirmPopup(true);
  };

  // Handle confirmed Undo
  const handleUndoConfirm = async () => {
    if (!undoAccountPeriodCloseOid) {
      setErrorMessage('AccountPeriodCloseOid олдсонгүй');
      setShowErrorPopup(true);
      setShowUndoConfirmPopup(false);
      return;
    }

    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        AccountPeriodCloseOid: undoAccountPeriodCloseOid
      });
      
      const result = await api.post(`/api/undo-account-period-close?${queryParams.toString()}`, {});
      
      if (result && result.success) {
        setWarningMessage('Хаалтын гүйлгээ амжилттай буцаагдлаа');
        setShowWarningAlert(true);
        // Refresh the list
        await fetchAccountPeriodClose();
      } else {
        setErrorMessage(result?.message || 'Хаалтын гүйлгээ буцаах явцад алдаа гарлаа');
        setShowErrorPopup(true);
      }
    } catch (error: any) {
      console.error('❌ Error undoing account period close:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Хаалтын гүйлгээ буцаах явцад алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
      setShowUndoConfirmPopup(false);
      setUndoAccountPeriodCloseOid(null);
    }
  };

  const handleAddCancel = () => {
    setShowAddPopup(false);
    setNewCloseData({
      CustomerOid: '',
      YearType: DEFAULT_YEAR_TYPE,
      PeriodType: '2',
      IsManual: '0',
      ManualItemCostAmount: '0',
      ClearOld: '1',
      UseRandom: '1'
    });
    setTrialBalanceData([]);
    setShowTrialBalance(false);
    setVatReportData([]);
    setControlReceiptData([]);
    setNoLedgerTransactionData([]);
    setJournalData([]);
    setShowJournalEntries(false);
    setShowJournalPopup(false);
    setEditingJournal(null);
    setIsPreviousYear(false);
    setIsWithVat(false);
    setJournalFormData({
      Date: '',
      Description: '',
      AccountId: '',
      OffsetAccountId: '',
      AmountDebit: '',
      AmountCredit: '',
      VatAmount: '',
      Amount: '',
      CashflowCategoryOid: '',
      YearType: '',
      PeriodType: '',
      DayType: ''
    });
    setCurrentStep(1);
    setAccountPeriodCloseResult(null);
    setAccountPeriodCloseItems([]);
    setBatch4Amount(null);
    setCostOfGoodsSold(null);
    step6ApisCalledRef.current = false; // Reset ref when popup is cancelled
  };

  // Fetch Control Receipt data
  const fetchControlReceipt = useCallback(async (customerOid: string, yearType: string, periodType?: string) => {
    if (!customerOid || !yearType) {
      setControlReceiptData([]);
      return;
    }

    try {
      setLoadingControlReceipt(true);
      
      const queryParams = new URLSearchParams({
        CustomerOid: customerOid,
        YearType: yearType
      });

      if (periodType && periodType !== '0') {
        const apiPeriodType = resolvePeriodTypeForApi(periodType);
        if (apiPeriodType) {
          queryParams.append('PeriodType', apiPeriodType);
        }
      }
      
      const apiUrl = `/api/control-receipt?${queryParams.toString()}`;
      
      console.log('🌐 Control Receipt API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} control receipt records`);
        setControlReceiptData(result.data);
      } else {
        setControlReceiptData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching Control Receipt:', error);
      setControlReceiptData([]);
    } finally {
      setLoadingControlReceipt(false);
    }
  }, []);

  // Fetch No Ledger Transaction List data
  const fetchNoLedgerTransactionList = useCallback(async (customerOid: string, yearType: string, periodType?: string) => {
    if (!customerOid || !yearType) {
      setNoLedgerTransactionData([]);
      return;
    }

    try {
      setLoadingNoLedgerTransaction(true);
      
      const queryParams = new URLSearchParams({
        CustomerOid: customerOid,
        yeartype: yearType
      });

      if (periodType && periodType !== '0') {
        const apiPeriodType = resolvePeriodTypeForApi(periodType);
        if (apiPeriodType) {
          queryParams.append('PeriodType', apiPeriodType);
        }
      }
      
      const apiUrl = `/api/no-ledger-transaction-list?${queryParams.toString()}`;
      
      console.log('🌐 No Ledger Transaction List API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} no ledger transaction records`);
        
        // Transform API response to match display fields
        const transformedData = result.data.map((item: any) => {
          // Use actual API values instead of calculating
          // For Sales: NonVATSalesAmount, VATPayableAmount, VATInclusiveSalesAmount
          // For Purchase: NonVATPurchaseAmount, VATReceivableAmount, VATInclusivePurchaseAmount
          let amount = null;
          let vatAmount = null;
          let totalAmount = null;
          
          if (item.ReceiptType === 'Sales') {
            amount = item.NonVATSalesAmount !== undefined && item.NonVATSalesAmount !== null 
              ? item.NonVATSalesAmount 
              : null;
            vatAmount = item.VATPayableAmount !== undefined && item.VATPayableAmount !== null 
              ? item.VATPayableAmount 
              : null;
            totalAmount = item.VATInclusiveSalesAmount !== undefined && item.VATInclusiveSalesAmount !== null 
              ? item.VATInclusiveSalesAmount 
              : null;
          } else if (item.ReceiptType === 'Purchase') {
            amount = item.NonVATPurchaseAmount !== undefined && item.NonVATPurchaseAmount !== null 
              ? item.NonVATPurchaseAmount 
              : null;
            vatAmount = item.VATReceivableAmount !== undefined && item.VATReceivableAmount !== null 
              ? item.VATReceivableAmount 
              : null;
            totalAmount = item.VATInclusivePurchaseAmount !== undefined && item.VATInclusivePurchaseAmount !== null 
              ? item.VATInclusivePurchaseAmount 
              : null;
          }
          
          return {
            ...item,
            ReceiptNumber: item.Number ? String(item.Number) : (item.ReceiptNumber || null),
            Description: item.Description || item.BusinessCustomerName || item.Note || null,
            Amount: amount,
            VATAmount: vatAmount,
            TotalAmount: totalAmount,
          };
        });
        
        setNoLedgerTransactionData(transformedData);
      } else {
        setNoLedgerTransactionData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching No Ledger Transaction List:', error);
      setNoLedgerTransactionData([]);
    } finally {
      setLoadingNoLedgerTransaction(false);
    }
  }, []);

  // Fetch VAT Control Report data
  const fetchVatReport = useCallback(async (customerOid: string, yearType: string, periodType?: string) => {
    if (!customerOid || !yearType) {
      setVatReportData([]);
      setVatReportSummary(null);
      return;
    }

    try {
      setLoadingVatReport(true);
      
      const queryParams = new URLSearchParams({
        CustomerOid: customerOid,
        YearType: yearType
      });

      if (periodType && periodType !== '0') {
        const apiPeriodType = resolvePeriodTypeForApi(periodType);
        if (apiPeriodType) {
          queryParams.append('PeriodType', apiPeriodType);
        }
      }
      
      const apiUrl = `/api/control-vat-report?${queryParams.toString()}`;
      
      console.log('🌐 VAT Control Report API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} VAT control report records`);
        setVatReportData(result.data);
        setVatReportSummary(result.summary || null);
      } else {
        setVatReportData([]);
        setVatReportSummary(null);
      }
    } catch (error: any) {
      console.error('❌ Error fetching VAT Control Report:', error);
      setVatReportData([]);
      setVatReportSummary(null);
    } finally {
      setLoadingVatReport(false);
    }
  }, []);

  // Refresh VAT report from tax system
  const refreshVatReportFromTaxSystem = useCallback(async (customerOid: string, yearType: string, periodType?: string) => {
    if (!customerOid || !yearType) {
      setVatReportData([]);
      setVatReportSummary(null);
      return;
    }

    try {
      setLoadingVatReport(true);
      
      const queryParams = new URLSearchParams({
        CustomerOid: customerOid,
        Year: yearType
      });
      
      const apiUrl = `/api/taxsystem/taxdata/tax-report-vat-details?${queryParams.toString()}`;
      
      console.log('🔄 Refreshing VAT Report from Tax System API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success) {
        console.log('✅ Tax system VAT details saved successfully:', result.message);
        
        // After successful save, automatically refresh the VAT control report
        console.log('🔄 Automatically refreshing VAT control report data...');
        await fetchVatReport(customerOid, yearType, periodType);
        
        // Show success message
        setWarningMessage('НӨАТ-ийн тайлан амжилттай шинэчлэгдлээ');
        setShowWarningAlert(true);
      } else {
        console.warn('⚠️ Tax system API returned unsuccessful response');
        setErrorMessage(result?.message || 'НӨАТ-ийн тайлан шинэчлэхэд алдаа гарлаа');
        setShowErrorPopup(true);
      }
    } catch (error: any) {
      console.error('❌ Error refreshing VAT Report from Tax System:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'НӨАТ-ийн тайлан шинэчлэхэд алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoadingVatReport(false);
    }
  }, [fetchVatReport]);

  // Fetch Accounts list
  const fetchAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      
      const apiUrl = `/api/account`;
      
      console.log('🌐 Accounts API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} accounts`);
        setAccounts(result.data);
      } else {
        setAccounts([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching Accounts:', error);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  // Fetch Cashflow Categories
  const fetchCashflowCategories = useCallback(async () => {
    try {
      setLoadingCashflowCategories(true);
      
      const apiUrl = `/api/cashflow-category`;
      
      console.log('🌐 Cashflow Categories API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} cashflow categories`);
        setCashflowCategories(result.data);
      } else {
        setCashflowCategories([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching Cashflow Categories:', error);
      setCashflowCategories([]);
    } finally {
      setLoadingCashflowCategories(false);
    }
  }, []);

  // Fetch Journal Entries data
  const fetchJournalEntries = useCallback(async (customerOid: string) => {
    if (!customerOid) {
      setJournalData([]);
      return;
    }

    try {
      setLoadingJournal(true);
      
      const queryParams = new URLSearchParams({
        customerOid: customerOid
      });
      
      const apiUrl = `/api/ledger/add-ledger/journal?${queryParams.toString()}`;
      
      console.log('🌐 Journal Entries API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} journal entries`);
        setJournalData(result.data);
      } else {
        setJournalData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching Journal Entries:', error);
      setJournalData([]);
    } finally {
      setLoadingJournal(false);
    }
  }, []);

  // Handle Add Journal Entry
  const handleAddJournal = () => {
    console.log('🟢 handleAddJournal called');
    setEditingJournal(null);
    setIsPreviousYear(false);
    setIsWithVat(false);
    const today = new Date();
    setJournalFormData({
      Date: today.toISOString().slice(0, 10),
      Description: '',
      AccountId: '',
      OffsetAccountId: '',
      AmountDebit: '0',
      AmountCredit: '0',
      VatAmount: '',
      Amount: '',
      CashflowCategoryOid: '',
      YearType: newCloseData.YearType || today.getFullYear().toString(),
      PeriodType: (today.getMonth() + 1).toString(),
      DayType: today.getDate().toString()
    });
    console.log('🟢 Setting showJournalPopup to true');
    setShowJournalPopup(true);
    // Fetch accounts if not already loaded
    if (accounts.length === 0) {
      fetchAccounts();
    }
    // Fetch cashflow categories if not already loaded
    if (cashflowCategories.length === 0) {
      fetchCashflowCategories();
    }
  };

  // Handle Previous Year checkbox change
  const handlePreviousYearChange = (checked: boolean) => {
    setIsPreviousYear(checked);
    if (checked) {
      // Set to December 31st of previous year
      const today = new Date();
      const previousYear = today.getFullYear() - 1;
      const previousYearDate = `${previousYear}-12-31`;
      
      setJournalFormData(prev => ({
        ...prev,
        Date: previousYearDate,
        YearType: previousYear.toString(),
        PeriodType: '12',
        DayType: '31'
      }));
    } else {
      // Restore to current date
      const today = new Date();
      setJournalFormData(prev => ({
        ...prev,
        Date: today.toISOString().slice(0, 10),

      }));
    }
  };

  // Handle Edit Journal Entry
  const handleEditJournal = (journal: JournalRecord) => {
    setEditingJournal(journal);
    setIsPreviousYear(false);
    // Set checkbox based on existing VAT data - check if VatAmount exists (not null/undefined) or if Amount exists
    setIsWithVat((journal.VatAmount !== null && journal.VatAmount !== undefined) || (journal.Amount !== null && journal.Amount !== undefined));
    const journalDate = journal.Date ? new Date(journal.Date) : new Date();
    const dateStr = journal.Date ? formatDate(journal.Date) : new Date().toISOString().slice(0, 10);
    const dateObj = new Date(dateStr);
    
    setJournalFormData({
      Date: dateStr,
      Description: journal.Description || '',
      AccountId: journal.CreditAccountOid || '',
      OffsetAccountId: journal.DebitAccountOid || '',
      AmountDebit: journal.Amount ? journal.Amount.toString() : '',
      AmountCredit: '0',
      VatAmount: journal.VatAmount ? journal.VatAmount.toString() : '',
      Amount: journal.Amount ? journal.Amount.toString() : '',
      CashflowCategoryOid: journal.CashflowCategoryOid || '',
      YearType: journal.YearType ? journal.YearType.toString() : dateObj.getFullYear().toString(),
      PeriodType: journal.PeriodType ? journal.PeriodType.toString() : (dateObj.getMonth() + 1).toString(),
      DayType: journal.PeriodType ? journalDate.getDate().toString() : dateObj.getDate().toString()
    });
    setShowJournalPopup(true);
  };

  // Handle Delete Journal Entry
  const handleDeleteJournal = async (journal: JournalRecord) => {
    if (!journal.Oid) {
      setErrorMessage('Journal Oid олдсонгүй');
      setShowErrorPopup(true);
      return;
    }

    if (!window.confirm('Та энэ бичлэгийг устгахдаа итгэлтэй байна уу?')) {
      return;
    }

    try {
      setLoadingJournal(true);
      
      const queryParams = new URLSearchParams({
        JournalId: journal.Oid
      });
      
      const result = await api.delete(`/api/ledger/add-ledger?${queryParams.toString()}`);
      
      if (result && result.success) {
        setWarningMessage('Бичлэг амжилттай устгагдлаа');
        setShowWarningAlert(true);
        
        // Refresh journal entries
        if (newCloseData.CustomerOid) {
          await fetchJournalEntries(newCloseData.CustomerOid);
        }
      } else {
        throw new Error(result?.message || 'Бичлэг устгахад алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error deleting journal entry:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Бичлэг устгахад алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoadingJournal(false);
    }
  };

  // Handle Save Journal Entry (Add or Edit)
  const handleSaveJournal = async () => {
    if (!newCloseData.CustomerOid) {
      setErrorMessage('Харилцагч сонгох шаардлагатай');
      setShowErrorPopup(true);
      return;
    }

    if (!journalFormData.Date || !journalFormData.Description || !journalFormData.AccountId) {
      setErrorMessage('Огноо, Тайлбар, Данс талбарууд шаардлагатай');
      setShowErrorPopup(true);
      return;
    }

    try {
      setLoadingJournal(true);

      const submitData: any = {
        CustomerOid: newCloseData.CustomerOid,
        Date: journalFormData.Date,
        Description: journalFormData.Description,
        DebitAccountOid: journalFormData.OffsetAccountId
      };

      if (journalFormData.AccountId) {
        submitData.CreditAccountOid = journalFormData.AccountId;
      }

      // AmountDebit and AmountCredit
      const amountDebit = journalFormData.AmountDebit ? parseFloat(journalFormData.AmountDebit) : 0;
      const amountCredit = journalFormData.AmountCredit ? parseFloat(journalFormData.AmountCredit) : 0;
      submitData.AmountDebit = amountDebit;
      submitData.AmountCredit = amountCredit;

      // Amount from "Dyn oruulah" field (AmountDebit) - maps to Amount in JSON
      if (journalFormData.AmountDebit) {
        const amount = parseFloat(journalFormData.AmountDebit);
        submitData.Amount = amount;
      }

      // VatAmount and Amount from НӨАТ-тэй field (overrides Amount if both are set)
      if (journalFormData.VatAmount) {
        const vatAmount = parseFloat(journalFormData.VatAmount);
        submitData.VatAmount = vatAmount;
      }

      if (journalFormData.Amount) {
        const amount = parseFloat(journalFormData.Amount);
        submitData.Amount = amount; // This will override Amount from AmountDebit if both are set
      }

      if (journalFormData.CashflowCategoryOid) {
        submitData.CashflowCategoryOid = journalFormData.CashflowCategoryOid;
      }

      let result;
      if (editingJournal) {
        // Edit mode - PUT request
        const queryParams = new URLSearchParams({
          JournalId: editingJournal.Oid
        });
        
        result = await api.put(`/api/ledger/add-ledger?${queryParams.toString()}`, submitData);
      } else {
        // Add mode - POST request
        const queryParams = new URLSearchParams({
          CustomerOid: newCloseData.CustomerOid
        });
        
        result = await api.post(`/api/ledger/add-ledger?${queryParams.toString()}`, submitData);
      }

      if (result && result.success) {
        setWarningMessage(editingJournal ? 'Бичлэг амжилттай шинэчлэгдлээ' : 'Бичлэг амжилттай нэмэгдлээ');
        setShowWarningAlert(true);
        setShowJournalPopup(false);
        
        // Reset form
        setEditingJournal(null);
        setJournalFormData({
          Date: '',
          Description: '',
          AccountId: '',
          OffsetAccountId: '',
          AmountDebit: '',
          AmountCredit: '',
          VatAmount: '',
          Amount: '',
          CashflowCategoryOid: '',
          YearType: '',
          PeriodType: '',
          DayType: ''
        });
        
        // Refresh journal entries
        if (newCloseData.CustomerOid) {
          await fetchJournalEntries(newCloseData.CustomerOid);
        }
      } else {
        throw new Error(result?.message || 'Бичлэг хадгалахад алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error saving journal entry:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Бичлэг хадгалахад алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoadingJournal(false);
    }
  };

  // Handle Ledger Rebuild button click
  const handleRebuildLedger = useCallback(async (e?: React.MouseEvent) => {
    // STRICT: Only allow manual button clicks - block ALL automatic calls
    if (!e) {
      console.log('🚫 Blocked automatic rebuild call - only manual button clicks allowed');
      return;
    }

    // Prevent default behavior and stop propagation
    e.preventDefault();
    e.stopPropagation();

    // Multiple guards to prevent concurrent calls
    const now = Date.now();
    const timeSinceLastRebuild = now - lastRebuildTimeRef.current;
    const MIN_REBUILD_INTERVAL = 10000; // 10 seconds minimum between rebuilds

    // Check if already rebuilding - STRICT CHECK (multiple guards)
    if (isRebuildingRef.current || rebuildingLedger || activeRebuildPromiseRef.current) {
      console.log('⚠️ Ledger rebuild already in progress, ignoring call', {
        isRebuildingRef: isRebuildingRef.current,
        rebuildingLedger: rebuildingLedger,
        activePromise: !!activeRebuildPromiseRef.current
      });
      return;
    }

    // Check minimum interval - STRICT CHECK
    if (timeSinceLastRebuild < MIN_REBUILD_INTERVAL && lastRebuildTimeRef.current > 0) {
      console.log(`⚠️ Rebuild called too soon (${timeSinceLastRebuild}ms ago), minimum interval is ${MIN_REBUILD_INTERVAL}ms`);
      setWarningMessage(`Та түр хүлээгээд дахин оролдоно уу (${Math.ceil((MIN_REBUILD_INTERVAL - timeSinceLastRebuild) / 1000)} секунд)`);
      setShowWarningAlert(true);
      return;
    }

    // Reset attempted flag on manual button click ONLY if enough time has passed
    // This prevents rapid re-clicks that could cause multiple requests
    if (rebuildAttemptedRef.current && timeSinceLastRebuild >= MIN_REBUILD_INTERVAL) {
      console.log('🔄 Manual retry after previous attempt, resetting flag');
      rebuildAttemptedRef.current = false;
    } else if (rebuildAttemptedRef.current && timeSinceLastRebuild < MIN_REBUILD_INTERVAL) {
      console.log('🚫 Rebuild attempted recently, blocking rapid re-click');
      setWarningMessage(`Та түр хүлээгээд дахин оролдоно уу (${Math.ceil((MIN_REBUILD_INTERVAL - timeSinceLastRebuild) / 1000)} секунд)`);
      setShowWarningAlert(true);
      return;
    }

    if (!newCloseData.CustomerOid || !newCloseData.YearType) {
      setErrorMessage('Харилцагч болон жил сонгох шаардлагатай');
      setShowErrorPopup(true);
      return;
    }

    try {
      // Generate unique request ID to prevent duplicate calls
      const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Check if there's already a request in progress with a different ID
      if (rebuildRequestIdRef.current && rebuildRequestIdRef.current !== requestId) {
        console.log('🚫 Another rebuild request is already in progress, ignoring duplicate call');
        return;
      }
      
      // Set all guards immediately
      lastRebuildTimeRef.current = now;
      isRebuildingRef.current = true;
      rebuildAttemptedRef.current = true; // Mark as attempted
      rebuildRequestIdRef.current = requestId; // Set current request ID
      setRebuildingLedger(true);
      
      const queryParams = new URLSearchParams({
        CustomerOid: newCloseData.CustomerOid,
        Year: newCloseData.YearType
      });
      
      console.log('🔄 Starting ledger rebuild...', { 
        CustomerOid: newCloseData.CustomerOid, 
        Year: newCloseData.YearType,
        triggeredBy: 'button click',
        requestId: requestId,
        timestamp: new Date().toISOString()
      });
      setLedgerRebuildError(null); // Clear previous error
      
      // Step 1: Check receipt count first
      const countQueryParams = new URLSearchParams({
        CustomerOid: newCloseData.CustomerOid,
        yearType: newCloseData.YearType
      });
      
      console.log('🔍 Checking receipt count...', {
        CustomerOid: newCloseData.CustomerOid,
        yearType: newCloseData.YearType
      });
      
      const countResult = await api.get(`/api/cron/receipts/count?${countQueryParams.toString()}`);
      
      if (!countResult || !countResult.success) {
        setErrorMessage('Receipt count шалгахад алдаа гарлаа');
        setShowErrorPopup(true);
        // Clear guards on error
        isRebuildingRef.current = false;
        setRebuildingLedger(false);
        rebuildRequestIdRef.current = null;
        activeRebuildPromiseRef.current = null;
        return;
      }
      
      const receiptCount = countResult.count || 0;
      const receiptCountMessage = countResult.message || 'Баримт олдсонгүй';
      console.log('📊 Receipt count:', receiptCount);
      console.log('📋 Receipt count message:', receiptCountMessage);
      
      // If count < 1, show message and stop
      if (receiptCount < 1) {
        console.log('⚠️ Receipt count < 1, showing warning popup:', receiptCountMessage);
        // Use message from API response, or default to "Баримт олдсонгүй"
        setWarningMessage(receiptCountMessage);
        setShowWarningAlert(true);
        console.log('✅ Warning popup state set:', { message: receiptCountMessage, showAlert: true });
        // Clear guards when no receipts found
        isRebuildingRef.current = false;
        setRebuildingLedger(false);
        rebuildRequestIdRef.current = null;
        activeRebuildPromiseRef.current = null;
        rebuildAttemptedRef.current = false; // Allow retry
        return;
      }
      
      // Step 2: If count >= 1, proceed with ledger rebuild
      console.log('✅ Receipt count >= 1, proceeding with ledger rebuild...');
      
      // Create and store the promise to prevent duplicate calls
      // Note: This API can take up to 6 minutes (360 seconds) to complete
      const rebuildPromise = api.post(`/api/cron/ledger/rebuild/customer?${queryParams.toString()}`, {});
      activeRebuildPromiseRef.current = rebuildPromise;
      
      console.log('⏳ Waiting for ledger rebuild response (this may take several minutes)...');
      
      const result = await rebuildPromise;
      
      console.log('📥 Ledger rebuild response received:', {
        success: result?.success,
        hasResult: !!result,
        resultKeys: result ? Object.keys(result) : []
      });
      
      if (result && result.success) {
        // Show success message with details
        const successDetails = `Амжилттай: ${result.successCount || 0}, Боловсруулсан: ${result.processed || 0}, Алдаа: ${result.errors || 0}`;
        const successMessage = `Ledger амжилттай дахин бүтээгдлээ (${successDetails})`;
        setWarningMessage(successMessage);
        setShowWarningAlert(true);
        setLedgerRebuildError(null); // Clear error on success
        rebuildAttemptedRef.current = false; // Reset on success to allow retry
        
        console.log('✅ Ledger rebuild амжилттай дууслаа - 200 статус код хүлээн авлаа');
        console.log('📊 Response data:', {
          success: result.success,
          processed: result.processed,
          successCount: result.successCount,
          errors: result.errors,
          message: result.message
        });
        
        // Refresh no ledger transaction list after rebuild (call directly, not through dependency)
        if (newCloseData.CustomerOid && newCloseData.YearType) {
          try {
            await fetchNoLedgerTransactionList(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
          } catch (refreshError) {
            console.error('Error refreshing transaction list after rebuild:', refreshError);
            // Don't throw - rebuild was successful
          }
        }
      } else {
        // API returned success: false - check for template error message
        const errorMessage = result?.message || result?.error || '';
        const errorDetails = result?.errorDetails || [];
        
        console.log('🔍 API returned success: false', {
          errorMessage,
          errorDetails,
          result
        });
        
        // Check for the new template error message from backend
        const isTemplateError = errorMessage === 'Админтай холбогдож гүйлгээний загвар тохируулана уу.' ||
          errorMessage.includes('Админтай холбогдож гүйлгээний загвар тохируулана уу.');
        
        // Check for old Sales template error messages (backward compatibility)
        const hasSalesTemplateError = errorMessage.includes('Sales template must return exactly 1 row') ||
          errorMessage.includes('Гүйлгээний загвар тохируулах шаардлагатай');
        
        // Check error details array - each error object has an 'error' property
        const salesTemplateErrorInDetails = Array.isArray(errorDetails) && errorDetails.length > 0
          ? errorDetails.find((err: any) => {
              if (typeof err === 'string') {
                return err.includes('Sales template must return exactly 1 row') || 
                       err.includes('Гүйлгээний загвар тохируулах шаардлагатай');
              }
              if (err && typeof err === 'object') {
                const errMsg = err.error || err.message || '';
                return errMsg.includes('Sales template must return exactly 1 row') || 
                       errMsg.includes('Гүйлгээний загвар тохируулах шаардлагатай');
              }
              return false;
            })
          : null;
        
        console.log('🔍 Template error check (success: false):', {
          isTemplateError,
          hasSalesTemplateError,
          salesTemplateErrorInDetails,
          errorMessage,
          errorDetailsLength: Array.isArray(errorDetails) ? errorDetails.length : 0
        });
        
        if (isTemplateError || hasSalesTemplateError || salesTemplateErrorInDetails) {
          // Show error in WarningPopup - use the message from API if available
          const templateMessage = isTemplateError 
            ? errorMessage 
            : 'Гүйлгээний загвар тохируулах шаардлагатай';
          console.log('✅ Setting warning message and showing popup (success: false):', templateMessage);
          setWarningMessage(templateMessage);
          setShowWarningAlert(true);
          setLedgerRebuildError(null); // Clear any previous error
          console.log('⚠️ Template error detected, showing in WarningPopup');
        } else {
          // Other errors - show in popup
          console.log('❌ Not a template error, showing error popup instead');
          setErrorMessage(errorMessage || 'Ledger дахин бүтээхэд алдаа гарлаа');
          setShowErrorPopup(true);
          setLedgerRebuildError(null);
        }
      }
    } catch (error: any) {
      console.error('Error rebuilding ledger:', error);
      
      // Handle receipt count API errors
      if (error.message?.includes('receipts/count') || error.response?.config?.url?.includes('receipts/count')) {
        setErrorMessage('Receipt count шалгахад алдаа гарлаа: ' + (error.response?.data?.error || error.message || 'Алдаа'));
        setShowErrorPopup(true);
        // Clear guards on error
        isRebuildingRef.current = false;
        setRebuildingLedger(false);
        rebuildRequestIdRef.current = null;
        activeRebuildPromiseRef.current = null;
        rebuildAttemptedRef.current = false; // Allow retry
        return;
      }
      
      // Handle 409 Conflict (rebuild already in progress) - DO NOT retry
      if (error.response?.status === 409 || error.response?.data?.inProgress) {
        const elapsedSeconds = error.response?.data?.elapsedSeconds || 0;
        setWarningMessage(`Ledger rebuild аль хэдийн ажиллаж байна. ${elapsedSeconds} секунд өмнө эхэлсэн. Түр хүлээгээд дахин оролдоно уу.`);
        setShowWarningAlert(true);
        setLedgerRebuildError(null);
        // Keep guards active to prevent retries
        rebuildAttemptedRef.current = true;
        return; // Exit early, don't clear guards immediately
      } else {
        // Check error response data for template error
        const errorData = error.response?.data || {};
        const errorMessage = errorData.message || errorData.error || error.message || '';
        const errorDetails = errorData.errorDetails || [];
        
        // Debug logging - comprehensive error structure check
        console.log('🔍 Error handling - Full error object:', {
          status: error.response?.status,
          errorData,
          errorMessage,
          errorDetails,
          errorResponse: error.response,
          fullError: error
        });
        
        // Check for the new template error message from backend
        const isTemplateError = errorMessage === 'Админтай холбогдож гүйлгээний загвар тохируулана уу.' ||
          errorMessage.includes('Админтай холбогдож гүйлгээний загвар тохируулана уу.');
        
        // Check error message in multiple places (backward compatibility)
        const hasSalesTemplateError = 
          errorMessage.includes('Sales template must return exactly 1 row') ||
          errorMessage.includes('Гүйлгээний загвар тохируулах шаардлагатай') ||
          error.message?.includes('Sales template must return exactly 1 row') ||
          error.message?.includes('Гүйлгээний загвар тохируулах шаардлагатай');
        
        // Check error details array
        const salesTemplateErrorInDetails = Array.isArray(errorDetails) && errorDetails.length > 0
          ? errorDetails.find((err: any) => {
              if (typeof err === 'string') {
                return err.includes('Sales template must return exactly 1 row') || 
                       err.includes('Гүйлгээний загвар тохируулах шаардлагатай');
              }
              if (err && typeof err === 'object') {
                const errMsg = err.error || err.message || err.errorMessage || '';
                return errMsg.includes('Sales template must return exactly 1 row') || 
                       errMsg.includes('Гүйлгээний загвар тохируулах шаардлагатай');
              }
              return false;
            })
          : null;
        
        // Also check if errorDetails is a string or has nested structure
        let salesTemplateErrorInNestedDetails = false;
        if (errorDetails && typeof errorDetails === 'object' && !Array.isArray(errorDetails)) {
          const nestedErrorMsg = errorDetails.error || errorDetails.message || errorDetails.errorMessage || '';
          salesTemplateErrorInNestedDetails = nestedErrorMsg.includes('Sales template must return exactly 1 row') || 
                                             nestedErrorMsg.includes('Гүйлгээний загвар тохируулах шаардлагатай');
        }
        
        console.log('🔍 Template error check (catch block):', {
          isTemplateError,
          hasSalesTemplateError,
          salesTemplateErrorInDetails,
          salesTemplateErrorInNestedDetails,
          errorMessage,
          errorDetailsLength: Array.isArray(errorDetails) ? errorDetails.length : 'not array',
          errorDetailsType: typeof errorDetails
        });
        
        if (isTemplateError || hasSalesTemplateError || salesTemplateErrorInDetails || salesTemplateErrorInNestedDetails) {
          // Show error in WarningPopup - use the message from API if available
          const templateMessage = isTemplateError 
            ? errorMessage 
            : 'Гүйлгээний загвар тохируулах шаардлагатай';
          console.log('✅ Setting warning message and showing popup (catch block):', templateMessage);
          setWarningMessage(templateMessage);
          setShowWarningAlert(true);
          setLedgerRebuildError(null); // Clear any previous error
          console.log('⚠️ Template error detected in catch block, showing in WarningPopup');
          // Keep guards active to prevent automatic retries on template errors
          rebuildAttemptedRef.current = true;
        } else {
          console.log('❌ Not a template error, showing error popup instead');
          setErrorMessage(errorMessage || 'Ledger дахин бүтээхэд алдаа гарлаа');
          setShowErrorPopup(true);
          setLedgerRebuildError(null);
          // Keep guards active to prevent automatic retries on other errors
          rebuildAttemptedRef.current = true;
        }
      }
    } finally {
      // IMPORTANT: Only clear guards AFTER a delay to prevent rapid re-triggers
      // This prevents the frontend from making multiple requests when errors occur
      // For errors, we keep rebuildAttemptedRef.current = true to block automatic retries
      setTimeout(() => {
        isRebuildingRef.current = false;
        setRebuildingLedger(false);
        rebuildRequestIdRef.current = null; // Clear request ID when done
        activeRebuildPromiseRef.current = null; // Clear active promise when done
        // Note: rebuildAttemptedRef.current stays true for errors to prevent retries
        // It will be reset only on success or when user manually clicks the button again
      }, 3000); // 3 second delay to prevent rapid re-triggers
    }
  }, [newCloseData.CustomerOid, newCloseData.YearType, rebuildingLedger]);

  // Handle Continue button from Control Receipt step - move to General Journal
  const handleControlReceiptContinue = () => {
    if (!newCloseData.CustomerOid || !newCloseData.YearType) {
      setErrorMessage('Харилцагч болон жил сонгох шаардлагатай');
      setShowErrorPopup(true);
      return;
    }
    // Move to step 2 (General Journal)
    setCurrentStep(2);
  };

  // Handle Continue button from General Journal step - move to VAT Report
  const handleGeneralJournalContinue = () => {
    if (!newCloseData.CustomerOid || !newCloseData.YearType) {
      setErrorMessage('Харилцагч болон жил сонгох шаардлагатай');
      setShowErrorPopup(true);
      return;
    }
    // Move to step 3 (VAT Control Report)
    setCurrentStep(3);
  };

  // Handle Continue button from VAT Report step - move to Trial Balance
  const handleVatReportContinue = () => {
    if (!newCloseData.CustomerOid || !newCloseData.YearType) {
      setErrorMessage('Харилцагч болон жил сонгох шаардлагатай');
      setShowErrorPopup(true);
      return;
    }
    // Move to step 4 (Trial Balance)
    setCurrentStep(4);
  };

  // Handle Continue button - POST request and show result
  const handleContinue = async () => {
    try {
      if (!newCloseData.CustomerOid) {
        setErrorMessage('Харилцагч сонгох шаардлагатай');
        setShowErrorPopup(true);
        return;
      }

      if (!newCloseData.YearType) {
        setErrorMessage('Жил оруулах шаардлагатай');
        setShowErrorPopup(true);
        return;
      }

      setLoading(true);

      // Default values for removed fields
      const submitData = {
        CustomerOid: newCloseData.CustomerOid,
        YearType: parseInt(newCloseData.YearType),
        PeriodType: getAccountPeriodClosePeriodType(newCloseData.PeriodType),
        IsManual: 0, // Default: үгүй
        ManualItemCostAmount: 0, // Default: 0
        ClearOld: 1, // Default: тийм
        UseRandom: 1 // Default: тийм
      };

      const queryParams = buildAccountPeriodCloseQueryParams(
        newCloseData.CustomerOid,
        newCloseData.YearType,
        newCloseData.PeriodType
      );

      const result = await api.post(`/api/account-period-close?${queryParams.toString()}`, submitData);

      if (result && result.success) {
        // Save result and move to step 2
        setAccountPeriodCloseResult(result);
        if (result.data && result.data.recordset) {
          const items = result.data.recordset;
          console.log('📊 AccountPeriodClose items:', items.length, 'items');
          if (items.length > 0) {
            console.log('📊 First item sample:', {
              AccountOid: items[0].AccountOid,
              AccountCode: items[0].AccountCode,
              AccountName: items[0].AccountName
            });
          }
          setAccountPeriodCloseItems(items);
          
          // Batch 4-ийн "Борлуулсан бүтээгдэхүүний өртөг (610100)" мөрний дүнг олох
          const targetAccountOid = 'C52D0C76-8A0D-4EDA-BEFB-6CD6F52C139F';
          const targetItem = items.find((item: AccountPeriodCloseItemRecord) => 
            item.BatchNumber === 4 && 
            item.AccountOid === targetAccountOid &&
            (item.Description?.includes('610100') || item.Description?.includes('Борлуулсан бүтээгдэхүүний өртөг'))
          );
          
          if (targetItem) {
            // DebitAmountC2 эсвэл CreditAmountC2-ийг авна
            const amount = targetItem.DebitAmountC2 || targetItem.CreditAmountC2 || targetItem.Amount || 0;
            setCostOfGoodsSold(amount);
          } else {
            // Хэрэв олдохгүй бол Batch 4-ийн эхний дүнг авна
            const batch4Items = items.filter((item: AccountPeriodCloseItemRecord) => item.BatchNumber === 4);
            if (batch4Items.length > 0) {
              const firstItem = batch4Items[0];
              const initialAmount = firstItem.CreditAmountC2 || firstItem.DebitAmountC2 || 0;
              setBatch4Amount(initialAmount);
            }
          }
        }
        setCurrentStep(5);
        
        // Show warning if it's a warning response
        if (result.warning) {
          setWarningMessage(result.message || 'Хаалт аль хэдийн хийгдсэн байна');
          setShowWarningAlert(true);
        }
      } else {
        throw new Error(result?.message || 'Хаалтын гүйлгээ үүсгэхэд алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error creating account period close:', error);
      
      // Handle warning response (200 status with warning flag)
      if (error.response?.status === 200 && error.response?.data?.warning) {
        setWarningMessage(error.response.data.message || 'Хаалт аль хэдийн хийгдсэн байна');
        setShowWarningAlert(true);
        // Still show the result if available
        if (error.response.data.data) {
          setAccountPeriodCloseResult(error.response.data);
          if (error.response.data.data.recordset) {
            const items = error.response.data.data.recordset;
            setAccountPeriodCloseItems(items);
            
            // Batch 4-ийн "Борлуулсан бүтээгдэхүүний өртөг (610100)" мөрний дүнг олох
            const targetAccountOid = 'C52D0C76-8A0D-4EDA-BEFB-6CD6F52C139F';
            const targetItem = items.find((item: AccountPeriodCloseItemRecord) => 
              item.BatchNumber === 4 && 
              item.AccountOid === targetAccountOid &&
              (item.Description?.includes('610100') || item.Description?.includes('Борлуулсан бүтээгдэхүүний өртөг'))
            );
            
            if (targetItem) {
              const amount = targetItem.DebitAmountC2 || targetItem.CreditAmountC2 || targetItem.Amount || 0;
              setCostOfGoodsSold(amount);
            } else {
              // Хэрэв олдохгүй бол Batch 4-ийн эхний дүнг авна
              const batch4Items = items.filter((item: AccountPeriodCloseItemRecord) => item.BatchNumber === 4);
              if (batch4Items.length > 0) {
                const firstItem = batch4Items[0];
                const initialAmount = firstItem.CreditAmountC2 || firstItem.DebitAmountC2 || 0;
                setBatch4Amount(initialAmount);
              }
            }
          }
          setCurrentStep(5);
        }
        return;
      }
      
      setErrorMessage(error.response?.data?.message || error.message || 'Хаалтын гүйлгээ үүсгэхэд алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle Calculate button - POST request with ManualItemCostAmount
  const handleCalculate = async () => {
    try {
      if (!newCloseData.CustomerOid) {
        setErrorMessage('Харилцагч сонгох шаардлагатай');
        setShowErrorPopup(true);
        return;
      }

      if (!newCloseData.YearType) {
        setErrorMessage('Жил оруулах шаардлагатай');
        setShowErrorPopup(true);
        return;
      }

      if (costOfGoodsSold === null || costOfGoodsSold === 0) {
        setErrorMessage('Борлуулсан бүтээгдэхүүний өртөг оруулах шаардлагатай');
        setShowErrorPopup(true);
        return;
      }

      setLoading(true);

      const submitData = {
        CustomerOid: newCloseData.CustomerOid,
        YearType: parseInt(newCloseData.YearType),
        PeriodType: getAccountPeriodClosePeriodType(newCloseData.PeriodType),
        IsManual: 1, // Manual mode идэвхжүүлэх
        ManualItemCostAmount: costOfGoodsSold, // Input field-аас авсан утга
        ClearOld: 1,
        UseRandom: 1
      };

      const queryParams = buildAccountPeriodCloseQueryParams(
        newCloseData.CustomerOid,
        newCloseData.YearType,
        newCloseData.PeriodType
      );

      const result = await api.post(`/api/account-period-close?${queryParams.toString()}`, submitData);

      if (result && result.success) {
        // Save result and move to step 4
        setAccountPeriodCloseResult(result);
        if (result.data && result.data.recordset) {
          let items = result.data.recordset;
          
          // Batch 4-ийн мөрүүдийн дүнг input field-аас авсан утгаар солих
          items = items.map((item: AccountPeriodCloseItemRecord) => {
            if (item.BatchNumber === 4) {
              // Эхний мөр (CreditAmountC2 байгаа)
              if (item.CreditAmountC2 && item.CreditAmountC2 > 0) {
                return {
                  ...item,
                  CreditAmountC2: costOfGoodsSold,
                  Amount: costOfGoodsSold
                };
              }
              // Хоёр дахь мөр (DebitAmountC2 байгаа)
              if (item.DebitAmountC2 && item.DebitAmountC2 > 0) {
                return {
                  ...item,
                  DebitAmountC2: costOfGoodsSold,
                  Amount: costOfGoodsSold
                };
              }
            }
            return item;
          });
          
          setAccountPeriodCloseItems(items);
          
          // Batch 4-ийн "Борлуулсан бүтээгдэхүүний өртөг (610100)" мөрний дүнг олох
          const targetAccountOid = 'C52D0C76-8A0D-4EDA-BEFB-6CD6F52C139F';
          const targetItem = items.find((item: AccountPeriodCloseItemRecord) => 
            item.BatchNumber === 4 && 
            item.AccountOid === targetAccountOid &&
            (item.Description?.includes('610100') || item.Description?.includes('Борлуулсан бүтээгдэхүүний өртөг'))
          );
          
          if (targetItem) {
            // DebitAmountC2 эсвэл CreditAmountC2-ийг авна
            const amount = targetItem.DebitAmountC2 || targetItem.CreditAmountC2 || targetItem.Amount || 0;
            setCostOfGoodsSold(amount);
          } else {
            // Хэрэв олдохгүй бол Batch 4-ийн эхний дүнг авна
            const batch4Items = items.filter((item: AccountPeriodCloseItemRecord) => item.BatchNumber === 4);
            if (batch4Items.length > 0) {
              const firstItem = batch4Items[0];
              const initialAmount = firstItem.CreditAmountC2 || firstItem.DebitAmountC2 || 0;
              setBatch4Amount(initialAmount);
            }
          }
        }
        setCurrentStep(5);
        
        // Show warning if it's a warning response
        if (result.warning) {
          setWarningMessage(result.message || 'Хаалт аль хэдийн хийгдсэн байна');
          setShowWarningAlert(true);
        }
      } else {
        throw new Error(result?.message || 'Хаалтын гүйлгээ үүсгэхэд алдаа гарлаа');
      }
    } catch (error: any) {
      console.error('Error creating account period close:', error);
      
      // Handle warning response (200 status with warning flag)
      if (error.response?.status === 200 && error.response?.data?.warning) {
        setWarningMessage(error.response.data.message || 'Хаалт аль хэдийн хийгдсэн байна');
        setShowWarningAlert(true);
        // Still show the result if available
        if (error.response.data.data) {
          setAccountPeriodCloseResult(error.response.data);
          if (error.response.data.data.recordset) {
            let items = error.response.data.data.recordset;
            
            // Batch 4-ийн мөрүүдийн дүнг input field-аас авсан утгаар солих
            if (costOfGoodsSold !== null && costOfGoodsSold > 0) {
              items = items.map((item: AccountPeriodCloseItemRecord) => {
                if (item.BatchNumber === 4) {
                  // Эхний мөр (CreditAmountC2 байгаа)
                  if (item.CreditAmountC2 && item.CreditAmountC2 > 0) {
                    return {
                      ...item,
                      CreditAmountC2: costOfGoodsSold,
                      Amount: costOfGoodsSold
                    };
                  }
                  // Хоёр дахь мөр (DebitAmountC2 байгаа)
                  if (item.DebitAmountC2 && item.DebitAmountC2 > 0) {
                    return {
                      ...item,
                      DebitAmountC2: costOfGoodsSold,
                      Amount: costOfGoodsSold
                    };
                  }
                }
                return item;
              });
            }
            
            setAccountPeriodCloseItems(items);
            
            // Batch 4-ийн "Борлуулсан бүтээгдэхүүний өртөг (610100)" мөрний дүнг олох
            const targetAccountOid = 'C52D0C76-8A0D-4EDA-BEFB-6CD6F52C139F';
            const targetItem = items.find((item: AccountPeriodCloseItemRecord) => 
              item.BatchNumber === 4 && 
              item.AccountOid === targetAccountOid &&
              (item.Description?.includes('610100') || item.Description?.includes('Борлуулсан бүтээгдэхүүний өртөг'))
            );
            
            if (targetItem) {
              const amount = targetItem.DebitAmountC2 || targetItem.CreditAmountC2 || targetItem.Amount || 0;
              setCostOfGoodsSold(amount);
            } else {
              // Хэрэв олдохгүй бол Batch 4-ийн эхний дүнг авна
              const batch4Items = items.filter((item: AccountPeriodCloseItemRecord) => item.BatchNumber === 4);
              if (batch4Items.length > 0) {
                const firstItem = batch4Items[0];
                const initialAmount = firstItem.CreditAmountC2 || firstItem.DebitAmountC2 || 0;
                setBatch4Amount(initialAmount);
              }
            }
          }
          setCurrentStep(5);
        }
        return;
      }
      
      setErrorMessage(error.response?.data?.message || error.message || 'Хаалтын гүйлгээ үүсгэхэд алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Татварлуу илгээх - tt-02 submit-all API дуудаж амжилт/алдаанд SuccessPopup/WarningPopup харуулна
  const handleSendToTax = async () => {
    const customerOid = newCloseData?.CustomerOid;
    if (!customerOid) {
      setWarningMessage('Харилцагч сонгоно уу.');
      setShowWarningAlert(true);
      return;
    }
    try {
      setLoading(true);
      const url = `/api/tax-report-send/tt-02/submit-all?CustomerOid=${encodeURIComponent(customerOid)}`;
      const response = await api.get(url);
      if (response?.success) {
        const msg = response.message || 'Татварлуу амжилттай илгээгдлээ.';
        setSuccessMessage(msg);
        setShowSuccessPopup(true);
      } else {
        const msg = response?.message || 'Татварлуу илгээхэд алдаа гарлаа.';
        setWarningMessage(msg);
        setShowWarningAlert(true);
      }
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.message || 'Татварлуу илгээхэд алдаа гарлаа.';
      setWarningMessage(msg);
      setShowWarningAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle Back button - go back to previous step
  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1); // From General Journal back to Control Receipt
    } else if (currentStep === 3) {
      setCurrentStep(2); // From VAT Report back to General Journal
    } else if (currentStep === 4) {
      setCurrentStep(3); // From Trial Balance back to VAT Report
    } else if (currentStep === 5) {
      setCurrentStep(4); // From Result back to Trial Balance
    } else if (currentStep === 6) {
      setCurrentStep(5); // From Final Status back to Result
      step6ApisCalledRef.current = false; // Reset ref when going back from step 6
    }
  };

  // Handle Save button click - show confirmation popup
  const handleSaveClick = () => {
    console.log('🔵 handleSaveClick called - showing confirmation popup');
    setShowConfirmPopup(true);
  };

  // Handle confirmed Save - navigate to step 6 (APIs will be called in useEffect when step 6 is entered)
  const handleFinalSave = () => {
    console.log('✅ handleFinalSave called - navigating to step 6');
    setShowConfirmPopup(false);
    // Reset the ref so APIs can be called when entering step 6
    step6ApisCalledRef.current = false;
    // Navigate to step 6, APIs will be called automatically via useEffect
    console.log('🔄 Setting currentStep to 6');
    setCurrentStep(6);
  };

  // Fetch Trial Balance Period data
  // Create Trial Balance for customer
  const createTrialBalanceForCustomer = useCallback(async (customerOid: string, year: string, periodType?: string) => {
    if (!customerOid || !year) {
      return;
    }

    try {
      const queryParams = new URLSearchParams({
        customerOid: customerOid,
        year: year
      });

      if (periodType && periodType !== '0') {
        const apiPeriodType = resolvePeriodTypeForApi(periodType);
        if (apiPeriodType) {
          queryParams.append('PeriodType', apiPeriodType);
        }
      }
      
      const apiUrl = `/api/trial-balance/create-one-customer?${queryParams.toString()}`;
      
      console.log('🔄 Creating Trial Balance for customer:', apiUrl);
      
      const result = await api.post(apiUrl, {});
      
      if (result && result.success) {
        console.log('✅ Trial Balance created successfully');
        return true;
      } else {
        console.warn('⚠️ Trial Balance creation returned non-success:', result);
        return false;
      }
    } catch (error: any) {
      console.error('❌ Error creating Trial Balance:', error);
      // Don't throw - allow to continue to fetch even if create fails
      return false;
    }
  }, []);

  const fetchTrialBalancePeriod = useCallback(async (customerOid: string, year: string, periodType?: string) => {
    if (!customerOid || !year) {
      setTrialBalanceData([]);
      setShowTrialBalance(false);
      return;
    }

    try {
      setLoadingTrialBalance(true);
      
      // Step 1: Fetch trial balance period data
      const queryParams = new URLSearchParams({
        CustomerOid: customerOid,
        Year: year
      });

      if (periodType && periodType !== '0') {
        const apiPeriodType = resolvePeriodTypeForApi(periodType);
        if (apiPeriodType) {
          queryParams.append('PeriodType', apiPeriodType);
        }
      }
      
      const apiUrl = `/api/trial-balance-period?${queryParams.toString()}`;
      
      console.log('🌐 Trial Balance API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} trial balance records`);
        let trialBalanceRecords: TrialBalancePeriodRecord[] = result.data;
        
        // Step 2: Fetch tax debit balance data (getAccountInfoByPeriod) - түр идэвхгүй
        /*
        try {
          const taxQueryParams = new URLSearchParams({
            CustomerOid: customerOid,
            YearType: year,
          });
          
          const taxApiUrl = `/api/taxsystem/taxdata/tax-debit-balance?${taxQueryParams.toString()}`;
          console.log('🌐 Tax Debit Balance API URL:', taxApiUrl);
          
          const taxResult = await api.get(taxApiUrl);
          
          if (taxResult && taxResult.success && taxResult.data && taxResult.data.accounts) {
            console.log(`✅ Fetched ${taxResult.data.accounts.length} tax debit balance records`);
            
            // Map taxTypeCode to account codes based on tax type and debit/credit
            // For some tax types, debit goes to one account and credit goes to another
            interface TaxAccountMapping {
              debitAccount?: string;  // Account code for debit amounts
              creditAccount?: string; // Account code for credit amounts
              defaultAccount?: string; // Default account if debit/credit not specified
            }
            
            const taxTypeToAccountMap: { [key: string]: TaxAccountMapping } = {
              // Дотоодын бараа, үйлчилгээний суутгасан НӨАТ
              // Debit -> 340200 НӨАТ-н өглөг, Credit -> 120500 НӨАТ-н авлага
              '01030101': {
                debitAccount: '340200',  // НӨАТ-н өглөг
                creditAccount: '120500'  // НӨАТ-н авлага
              },
              // Аж ахуйн нэгжийн орлогын албан татвар - ААНОАТ-н өглөг (340100)
              '01010201': {
                defaultAccount: '340100'
              },
              // НӨАТ-н өглөг - НӨАТ-н өглөг (340200)
              '01030201': {
                defaultAccount: '340200'
              },
              // Нийслэл хотын албан татвар - Нийслэл хотын албан татварын өглөг (340300)
              '01030710': {
                defaultAccount: '340300'
              },
              // Цалин, хөдөлмөрийн хөлс, тэдгээртэй адилтгах орлогоос суутгасан татвар - Хүн амын орлогын албан татварын өглөг (340400)
              '01010101': {
                defaultAccount: '340400'
              },
              // Хог хаягдлын үйлчилгээний хураамж - Бусад татварын өглөг (340900)
              '01030723': {
                defaultAccount: '340900'
              },
              // Улсын тэмдэгтийн хураамж - Бусад татварын өглөг (340900)
              '01030701': {
                defaultAccount: '340900'
              },
              // Суутгагчийн хуулийн этгээдэд олгосон орлогоос суутгасан татвар - Бусад татварын өглөг (340900)
              '01010203': {
                defaultAccount: '340900'
              },
            };
            
            // Create a map of account code to aggregated tax amounts
            const accountTaxMap = new Map<string, { c1Debit: number; c1Credit: number; c2Debit: number; c2Credit: number }>();
            
            taxResult.data.accounts.forEach((taxRecord: any) => {
              if (!taxRecord.taxTypeCode) return;
              
              const mapping = taxTypeToAccountMap[taxRecord.taxTypeCode];
              
              if (!mapping) {
                // Fallback: try to match by accountNo if available
                if (taxRecord.accountNo) {
                  const accountCode = taxRecord.accountNo;
                  const existing = accountTaxMap.get(accountCode) || { c1Debit: 0, c1Credit: 0, c2Debit: 0, c2Credit: 0 };
                  accountTaxMap.set(accountCode, {
                    c1Debit: existing.c1Debit + (taxRecord.c1Debit || 0),
                    c1Credit: existing.c1Credit + (taxRecord.c1Credit || 0),
                    c2Debit: existing.c2Debit + (taxRecord.c2Debit || 0),
                    c2Credit: existing.c2Credit + (taxRecord.c2Credit || 0),
                  });
                }
                return;
              }
              
              // Handle debit amounts
              if (taxRecord.c1Debit || taxRecord.c2Debit) {
                const debitAccount = mapping.debitAccount || mapping.defaultAccount;
                if (debitAccount) {
                  const existing = accountTaxMap.get(debitAccount) || { c1Debit: 0, c1Credit: 0, c2Debit: 0, c2Credit: 0 };
                  accountTaxMap.set(debitAccount, {
                    ...existing,
                    c1Debit: existing.c1Debit + (taxRecord.c1Debit || 0),
                    c2Debit: existing.c2Debit + (taxRecord.c2Debit || 0),
                  });
                }
              }
              
              // Handle credit amounts
              if (taxRecord.c1Credit || taxRecord.c2Credit) {
                const creditAccount = mapping.creditAccount || mapping.defaultAccount;
                if (creditAccount) {
                  const existing = accountTaxMap.get(creditAccount) || { c1Debit: 0, c1Credit: 0, c2Debit: 0, c2Credit: 0 };
                  accountTaxMap.set(creditAccount, {
                    ...existing,
                    c1Credit: existing.c1Credit + (taxRecord.c1Credit || 0),
                    c2Credit: existing.c2Credit + (taxRecord.c2Credit || 0),
                  });
                }
              }
              
              // If both debit and credit are null/zero, use default account
              if (!taxRecord.c1Debit && !taxRecord.c2Debit && !taxRecord.c1Credit && !taxRecord.c2Credit) {
                const defaultAccount = mapping.defaultAccount || mapping.debitAccount || mapping.creditAccount;
                if (defaultAccount) {
                  const existing = accountTaxMap.get(defaultAccount) || { c1Debit: 0, c1Credit: 0, c2Debit: 0, c2Credit: 0 };
                  accountTaxMap.set(defaultAccount, existing);
                }
              }
            });
            
            console.log('📊 Tax data mapping:', {
              totalTaxRecords: taxResult.data.accounts.length,
              accountTaxMapSize: accountTaxMap.size,
              accountTaxMap: Array.from(accountTaxMap.entries()).map(([code, amounts]) => ({
                accountCode: code,
                amounts
              }))
            });
            
            // Merge tax data into trial balance records
            trialBalanceRecords = trialBalanceRecords.map((record) => {
              const taxAmounts = accountTaxMap.get(record.AccountCode);
              return {
                ...record,
                TaxDebitC1: taxAmounts?.c1Debit || 0,
                TaxCreditC1: taxAmounts?.c1Credit || 0,
                TaxDebitC2: taxAmounts?.c2Debit || 0,
                TaxCreditC2: taxAmounts?.c2Credit || 0,
              };
            });
            
            console.log('✅ Merged tax data with trial balance records');
          } else {
            console.warn('⚠️ Tax data not available or empty, proceeding without tax data');
          }
        } catch (taxError: any) {
          console.error('❌ Error fetching Tax Debit Balance:', taxError);
          // Continue without tax data - don't fail the whole operation
        }
        */
        
        setTrialBalanceData(trialBalanceRecords);
        setShowTrialBalance(true);
      } else {
        setTrialBalanceData([]);
        setShowTrialBalance(false);
      }
    } catch (error: any) {
      console.error('❌ Error fetching Trial Balance Period:', error);
      setTrialBalanceData([]);
      setShowTrialBalance(false);
    } finally {
      setLoadingTrialBalance(false);
    }
  }, []);

  // Extract WT_CreditAmount from accountPeriodCloseItems when items are loaded
  useEffect(() => {
    if (accountPeriodCloseItems.length > 0 && (costOfGoodsSold === null || costOfGoodsSold === 0)) {
      // Find item with BatchNumber === 4 and AccountCode === "140100" to get WT_CreditAmount
      const targetItem = accountPeriodCloseItems.find((item: AccountPeriodCloseItemRecord) => 
        item.BatchNumber === 4 && 
        item.AccountCode === "140100"
      );
      
      if (targetItem && (targetItem.WT_CreditAmount !== null && targetItem.WT_CreditAmount !== undefined && targetItem.WT_CreditAmount > 0)) {
        setCostOfGoodsSold(targetItem.WT_CreditAmount);
        console.log('✅ useEffect: Set default costOfGoodsSold from WT_CreditAmount:', targetItem.WT_CreditAmount);
      } else {
        // Fallback: try to find by description
        const fallbackItem = accountPeriodCloseItems.find((item: AccountPeriodCloseItemRecord) => 
          item.BatchNumber === 4 && 
          (item.Description?.includes('Борлуулсан бүтээгдэхүүний өртөг') || 
           item.Description?.includes('Inventory') ||
           item.Description?.includes('Бараа материал'))
        );
        
        if (fallbackItem && (fallbackItem.WT_CreditAmount !== null && fallbackItem.WT_CreditAmount !== undefined && fallbackItem.WT_CreditAmount > 0)) {
          setCostOfGoodsSold(fallbackItem.WT_CreditAmount);
          console.log('✅ useEffect: Set default costOfGoodsSold from fallback WT_CreditAmount:', fallbackItem.WT_CreditAmount);
        } else {
          // Last fallback: first item in Batch 4 with WT_CreditAmount
          const batch4Items = accountPeriodCloseItems.filter((item: AccountPeriodCloseItemRecord) => item.BatchNumber === 4);
          if (batch4Items.length > 0) {
            const firstBatch4Item = batch4Items.find(item => item.WT_CreditAmount && item.WT_CreditAmount > 0) || batch4Items[0];
            if (firstBatch4Item && (firstBatch4Item.WT_CreditAmount !== null && firstBatch4Item.WT_CreditAmount !== undefined && firstBatch4Item.WT_CreditAmount > 0)) {
              setCostOfGoodsSold(firstBatch4Item.WT_CreditAmount);
              console.log('✅ useEffect: Set default costOfGoodsSold from first Batch 4 item WT_CreditAmount:', firstBatch4Item.WT_CreditAmount);
            }
          }
        }
      }
    }
  }, [accountPeriodCloseItems]);

  // Handle customer or year change in popup - fetch Control Receipt for step 1
  useEffect(() => {
    if (showAddPopup && currentStep === 1 && newCloseData.CustomerOid && newCloseData.YearType) {
      fetchControlReceipt(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
    } else if (showAddPopup && currentStep === 1) {
      setControlReceiptData([]);
    }
  }, [showAddPopup, currentStep, newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType, fetchControlReceipt]);

  // Handle customer or year change in popup - fetch No Ledger Transaction List for step 2
  useEffect(() => {
    if (showAddPopup && currentStep === 2 && newCloseData.CustomerOid && newCloseData.YearType) {
      fetchNoLedgerTransactionList(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
      // Also fetch journal entries when customer is selected
      if (newCloseData.CustomerOid) {
        fetchJournalEntries(newCloseData.CustomerOid);
      }
    } else if (showAddPopup && currentStep === 2) {
      setNoLedgerTransactionData([]);
      setJournalData([]);
    }
  }, [showAddPopup, currentStep, newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType, fetchNoLedgerTransactionList, fetchJournalEntries]);

  // Handle customer or year change in popup - fetch VAT report for step 3
  useEffect(() => {
    if (showAddPopup && currentStep === 3 && newCloseData.CustomerOid && newCloseData.YearType) {
      fetchVatReport(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
    } else if (showAddPopup && currentStep === 3) {
      setVatReportData([]);
      setVatReportSummary(null);
    }
  }, [showAddPopup, currentStep, newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType, fetchVatReport]);

  // Handle step 6 entry - call APIs when entering step 6
  useEffect(() => {
    console.log('🔍 Step 6 useEffect triggered:', { showAddPopup, currentStep, step6ApisCalled: step6ApisCalledRef.current });
    if (showAddPopup && currentStep === 6 && !step6ApisCalledRef.current) {
      console.log('✅ Step 6 conditions met, calling APIs...');
      step6ApisCalledRef.current = true; // Mark as called to prevent duplicate calls
      const callStep6Apis = async () => {
        try {
          // Get AccountPeriodCloseOid and Year from accountPeriodCloseResult or fetch from API
          let accountPeriodCloseOid: string | null = null;
          let customerOid = newCloseData.CustomerOid;
          let year: number | null = newCloseData.YearType ? parseInt(newCloseData.YearType) : null;

          // First, try to get from existing accountPeriodCloseResult
          if (accountPeriodCloseResult) {
            // Response structure: { success: true, data: [{ Oid: "...", YearType: 2025, ... }] }
            // AccountPeriodCloseOid is the Oid field in the data array
            if (accountPeriodCloseResult.data) {
              // Try data array first (new structure: data is an array)
              if (Array.isArray(accountPeriodCloseResult.data) && accountPeriodCloseResult.data.length > 0) {
                accountPeriodCloseOid = accountPeriodCloseResult.data[0].Oid || null;
                if (!year && accountPeriodCloseResult.data[0].YearType) {
                  year = parseInt(accountPeriodCloseResult.data[0].YearType);
                }
                console.log('📊 From accountPeriodCloseResult.data array - Oid (AccountPeriodCloseOid):', accountPeriodCloseOid, 'YearType (Year):', year);
              }
              // Try data.recordset (old structure)
              else if (accountPeriodCloseResult.data.recordset && accountPeriodCloseResult.data.recordset.length > 0) {
                accountPeriodCloseOid = accountPeriodCloseResult.data.recordset[0].Oid || accountPeriodCloseResult.data.recordset[0].AccountPeriodCloseOid || null;
                if (!year && accountPeriodCloseResult.data.recordset[0].YearType) {
                  year = parseInt(accountPeriodCloseResult.data.recordset[0].YearType);
                }
                console.log('📊 From accountPeriodCloseResult.data.recordset - AccountPeriodCloseOid:', accountPeriodCloseOid, 'YearType:', year);
              }
            }
            // Or try from parameters
            if (!accountPeriodCloseOid && accountPeriodCloseResult.parameters) {
              accountPeriodCloseOid = accountPeriodCloseResult.parameters.AccountPeriodCloseOid || null;
              customerOid = accountPeriodCloseResult.parameters.CustomerOid || customerOid;
              year = accountPeriodCloseResult.parameters.YearType || (accountPeriodCloseResult.parameters.Year ? parseInt(accountPeriodCloseResult.parameters.Year) : null) || year;
              console.log('📊 From accountPeriodCloseResult.parameters - AccountPeriodCloseOid:', accountPeriodCloseOid, 'Year:', year);
            }
          }

          // If AccountPeriodCloseOid is missing, fetch it from /api/account-period-close
          if (!accountPeriodCloseOid && customerOid) {
            console.log('🔍 AccountPeriodCloseOid missing, fetching from /api/account-period-close...');
            const queryParams = buildAccountPeriodCloseQueryParams(
              customerOid,
              (year ?? newCloseData.YearType).toString(),
              newCloseData.PeriodType
            );
            
            const fetchResult = await api.get(`/api/account-period-close?${queryParams.toString()}`);
            
            if (fetchResult && fetchResult.success && fetchResult.data) {
              // Response structure: { success: true, data: [{ Oid: "...", YearType: 2025, ... }] }
              // AccountPeriodCloseOid is the Oid field in the data array
              if (Array.isArray(fetchResult.data) && fetchResult.data.length > 0) {
                accountPeriodCloseOid = fetchResult.data[0].Oid || null;
                // Also get Year from YearType
                if (!year && fetchResult.data[0].YearType) {
                  year = parseInt(fetchResult.data[0].YearType);
                }
                console.log('📊 Fetched from data array - AccountPeriodCloseOid (Oid):', accountPeriodCloseOid, 'Year (YearType):', year);
              }
              // Try to get AccountPeriodCloseOid from data.recordset[0] (alternative structure)
              else if (fetchResult.data.recordset && fetchResult.data.recordset.length > 0) {
                accountPeriodCloseOid = fetchResult.data.recordset[0].Oid || fetchResult.data.recordset[0].AccountPeriodCloseOid || null;
                // Also get Year if available
                if (!year && fetchResult.data.recordset[0].YearType) {
                  year = parseInt(fetchResult.data.recordset[0].YearType);
                }
                console.log('📊 Fetched from recordset - AccountPeriodCloseOid:', accountPeriodCloseOid, 'Year:', year);
              }
              // Or try from parameters
              if (!accountPeriodCloseOid && fetchResult.parameters) {
                accountPeriodCloseOid = fetchResult.parameters.AccountPeriodCloseOid || null;
                year = fetchResult.parameters.YearType || (fetchResult.parameters.Year ? parseInt(fetchResult.parameters.Year) : null) || year;
                console.log('📊 Fetched from parameters - AccountPeriodCloseOid:', accountPeriodCloseOid, 'Year:', year);
              }
              
              console.log('✅ Final values - AccountPeriodCloseOid:', accountPeriodCloseOid, 'Year:', year);
            }
          }

          // Validate required parameters
          if (!accountPeriodCloseOid) {
            console.error('❌ AccountPeriodCloseOid is required but not found');
            setErrorMessage('AccountPeriodCloseOid олдсонгүй. Хаалтын гүйлгээг эхлүүлнэ үү.');
            setShowErrorPopup(true);
            return;
          }

          if (!year) {
            year = newCloseData.YearType ? parseInt(newCloseData.YearType) : null;
            if (!year) {
              console.error('❌ Year is required but not found');
              setErrorMessage('Жил олдсонгүй');
              setShowErrorPopup(true);
              return;
            }
          }

          setLoading(true);
          const apiPeriodType = resolvePeriodTypeForApi(newCloseData.PeriodType);
          console.log('🔄 Step 6: Calling APIs...', { accountPeriodCloseOid, customerOid, year, apiPeriodType });

          // Step 1: Call account-period-close-to-ledger with AccountPeriodCloseOid, Year, PostDate, and RebuildTrialBalance
          const postDate = getPeriodEndPostDate(year, newCloseData.PeriodType);
          const queryParams1 = new URLSearchParams({
            AccountPeriodCloseOid: accountPeriodCloseOid,
            Year: year.toString(),
            PostDate: postDate,
            RebuildTrialBalance: '1'
          });
          if (apiPeriodType) {
            queryParams1.append('PeriodType', apiPeriodType);
          }
          
          console.log('📤 Calling /api/account-period-close-to-ledger with:', queryParams1.toString());
          const result1 = await api.post(`/api/account-period-close-to-ledger?${queryParams1.toString()}`, {});
          
          if (!result1 || !result1.success) {
            throw new Error(result1?.message || 'Хаалтын гүйлгээг ledger-д оруулахад алдаа гарлаа');
          }
          console.log('✅ Step 1: account-period-close-to-ledger completed with AccountPeriodCloseOid and Year');

          // Ensure customerOid and year are available for subsequent API calls
          if (!customerOid || !year) {
            // Try to get from accountPeriodCloseResult if not already set
            if (accountPeriodCloseResult && accountPeriodCloseResult.parameters) {
              customerOid = accountPeriodCloseResult.parameters.CustomerOid || customerOid || newCloseData.CustomerOid;
              year = accountPeriodCloseResult.parameters.YearType || year || (newCloseData.YearType ? parseInt(newCloseData.YearType) : null);
            } else {
              customerOid = customerOid || newCloseData.CustomerOid;
              year = year || (newCloseData.YearType ? parseInt(newCloseData.YearType) : null);
            }

            if (!customerOid || !year) {
              console.warn('⚠️ CustomerOid or Year missing for subsequent APIs');
            }
          }

          // Step 2: Call trial-balance/create-one-customer
          const queryParams2 = new URLSearchParams({
            customerOid: customerOid,
            year: year.toString()
          });
          if (apiPeriodType) {
            queryParams2.append('PeriodType', apiPeriodType);
          }
          
          const result2 = await api.post(`/api/trial-balance/create-one-customer?${queryParams2.toString()}`, {});
          
          if (!result2 || !result2.success) {
            throw new Error(result2?.message || 'Trial Balance үүсгэхэд алдаа гарлаа');
          }
          console.log('✅ Step 2: trial-balance/create-one-customer completed');

          // Step 3: Call cashflow/create-one-customer
          const queryParams3 = new URLSearchParams({
            CustomerOid: customerOid,
            Year: year.toString()
          });
          if (apiPeriodType) {
            queryParams3.append('PeriodType', apiPeriodType);
          }
          
          const result3 = await api.post(`/api/cashflow/create-one-customer?${queryParams3.toString()}`, {});
          
          if (!result3 || !result3.success) {
            throw new Error(result3?.message || 'Cashflow үүсгэхэд алдаа гарлаа');
          }
          console.log('✅ Step 3: cashflow/create-one-customer completed');

          // Step 4: Fetch trial balance period data for Step 6
          try {
            const queryParamsPeriod = new URLSearchParams({
              CustomerOid: customerOid,
              Year: year.toString()
            });
            const apiPeriodType = resolvePeriodTypeForApi(newCloseData.PeriodType);
            if (apiPeriodType) {
              queryParamsPeriod.append('PeriodType', apiPeriodType);
            }
            
            const resultPeriod = await api.get(`/api/trial-balance-period?${queryParamsPeriod.toString()}`);
            
            if (resultPeriod && resultPeriod.success && resultPeriod.data) {
              // Store in a separate state for Step 6
              setTrialBalanceData(resultPeriod.data);
              setShowTrialBalance(true);
              console.log('✅ Step 6: trial-balance-period fetched successfully', resultPeriod.data.length, 'records');
            } else {
              console.warn('⚠️ Trial Balance Period мэдээлэл олдсонгүй');
            }
          } catch (periodError: any) {
            console.error('❌ Error fetching trial balance period:', periodError);
          }
        } catch (error: any) {
          console.error('❌ Error in Step 6 APIs:', error);
          // Even if some APIs fail, try to fetch trial balance year data
          try {
            let customerOid = newCloseData.CustomerOid;
            let year: number | null = null;

            if (accountPeriodCloseResult && accountPeriodCloseResult.parameters) {
              customerOid = accountPeriodCloseResult.parameters.CustomerOid || customerOid;
              year = accountPeriodCloseResult.parameters.YearType || null;
            }

            if (!customerOid) {
              customerOid = newCloseData.CustomerOid;
            }

            if (!year) {
              year = newCloseData.YearType ? parseInt(newCloseData.YearType) : null;
            }

            if (customerOid && year) {
              // Fetch TrialBalancePeriod
              try {
                const queryParamsPeriod = new URLSearchParams({
                  CustomerOid: customerOid,
                  Year: year.toString()
                });
                const apiPeriodType = resolvePeriodTypeForApi(newCloseData.PeriodType);
                if (apiPeriodType) {
                  queryParamsPeriod.append('PeriodType', apiPeriodType);
                }
                
                const resultPeriod = await api.get(`/api/trial-balance-period?${queryParamsPeriod.toString()}`);
                
                if (resultPeriod && resultPeriod.success && resultPeriod.data) {
                  setTrialBalanceData(resultPeriod.data);
                  setShowTrialBalance(true);
                  console.log('✅ Trial Balance Period fetched successfully after error recovery');
                }
              } catch (periodError) {
                console.error('❌ Error fetching trial balance period in recovery:', periodError);
              }
            }
          } catch (recoveryError) {
            console.error('❌ Error fetching trial balance year in recovery:', recoveryError);
          }
          
          setErrorMessage(error.response?.data?.message || error.message || 'Step 6 API дуудалтад алдаа гарлаа');
          setShowErrorPopup(true);
        } finally {
          setLoading(false);
        }
      };

      callStep6Apis();
    }
  }, [showAddPopup, currentStep, newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType, accountPeriodCloseResult]);

  // Handle customer or year change in popup - fetch Trial Balance for step 4
  useEffect(() => {
    if (showAddPopup && currentStep === 4 && newCloseData.CustomerOid && newCloseData.YearType) {
      // First create trial balance, then fetch it
      const initializeTrialBalance = async () => {
        try {
          setLoadingTrialBalance(true);
          
          // Step 1: Create trial balance for customer
          await createTrialBalanceForCustomer(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
          
          // Step 2: Fetch trial balance period data
          await fetchTrialBalancePeriod(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
        } catch (error) {
          console.error('Error initializing Trial Balance:', error);
        } finally {
          setLoadingTrialBalance(false);
        }
      };
      
      initializeTrialBalance();
    } else if (showAddPopup && currentStep === 4) {
      setTrialBalanceData([]);
      setShowTrialBalance(false);
    }
  }, [showAddPopup, currentStep, newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType, createTrialBalanceForCustomer, fetchTrialBalancePeriod]);

  // Create columns for AccountPeriodClose data
  const getColumns = (): SmartTableColumn[] => [
    { 
      key: 'CustomerID', 
      label: 'Харилцагчийн ID',
      render: (value: any) => value || '-'
    },
    { 
      key: 'CustomerName', 
      label: 'Харилцагчийн нэр',
      render: (value: any) => value || '-'
    },
    { 
      key: 'YearType', 
      label: 'Жил',
      render: (value: any) => value || '-'
    },
    { 
      key: 'PeriodType', 
      label: 'Сар',
      render: (value: any, row: any) => {
        if (value === 0) return 'Жил';
        return value || '-';
      }
    },
    { 
      key: 'CreateDate', 
      label: 'Үүсгэсэн огноо',
      render: (value: any) => formatDate(value)
    },
    { 
      key: 'IsManual', 
      label: 'Гараар',
      render: (value: any) => value === 1 ? 'Тийм' : 'Үгүй'
    },
    { 
      key: 'ManualItemCostAmount', 
      label: 'Гараар оруулсан зардлын дүн',
      render: (value: any) => formatNumber(value)
    },
    { 
      key: 'TaxableAmount', 
      label: 'Татвартай дүн',
      render: (value: any) => formatNumber(value)
    },
    { 
      key: 'TaxExpenseAmount', 
      label: 'Татварын зардал',
      render: (value: any) => formatNumber(value)
    },
    {
      key: 'actions',
      label: 'Үйлдэл',
      render: (value: any, row: AccountPeriodCloseRecord) => (
        <button
          onClick={() => handleUndoClick(row.Oid)}
          className="px-3 py-1 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          disabled={loading}
        >
          Устгах
        </button>
      )
    },
  ];

  useEffect(() => {
    // Fetch customers list on mount
    fetchCustomers();
    // Fetch accounts list on mount
    fetchAccounts();
    fetchCashflowCategories();
  }, [fetchCustomers, fetchAccounts, fetchCashflowCategories]);

  useEffect(() => {
    // Fetch account period close data when filters change
    fetchAccountPeriodClose();
  }, [fetchAccountPeriodClose]);

  // Customer dropdown options
  const customerOptions = [
    { value: '', label: 'Бүгд' },
    ...customers.map(customer => ({
      value: customer.Oid,
      label: `${customer.CustomerName || ''} (${customer.CustomerID || ''})`
    }))
  ];

  // Selected customer name for display
  const selectedCustomerName = selectedCustomerOid === '' 
    ? 'Бүгд'
    : customers.find(c => c.Oid === selectedCustomerOid) 
      ? `${customers.find(c => c.Oid === selectedCustomerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === selectedCustomerOid)?.CustomerID || ''})`
      : '';

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Хаалтын гүйлгээ</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={true}
            onAdd={handleAdd}
          />
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[150px]">
            <div className="text-sm text-gray-600">Нийт бичлэг</div>
            <div className="text-2xl font-bold text-gray-800">
              {closeData.length}
            </div>
          </div>
          <div className="flex-1 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Харилцагч сонгох
              </label>
              <SearchableDropdown
                options={customerOptions}
                value={selectedCustomerOid}
                onChange={(value) => setSelectedCustomerOid(value)}
                valueLabel={selectedCustomerName}
                placeholder="Харилцагч сонгох (Заавал биш)"
                className="w-full"
                enableAutoComplete={true}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Жил сонгох (Заавал биш)
              </label>
              <input
                type="text"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                placeholder="Жил оруулах (жишээ: 2025)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Уншиж байна...</p>
          </div>
        )}

        {!loading && (
          <SmartTable
            data={closeData}
            columns={getColumns()}
            fetchData={fetchAccountPeriodClose}
            settingsKey="account-period-close-table"
            editable={false}
            addable={true}
            onAdd={handleAdd}
            rowKey="Oid"
            showColumnSearch={showColumnSearch}
            showColumnSettings={showColumnSettings}
          />
        )}

        <ErrorPopup
          isVisible={showErrorPopup}
          message={errorMessage}
          onClose={() => setShowErrorPopup(false)}
        />

        <SuccessPopup
          isVisible={showSuccessPopup}
          message={successMessage}
          onClose={() => setShowSuccessPopup(false)}
        />

        <WarningPopup
          isVisible={showWarningAlert}
          message={warningMessage}
          onClose={() => setShowWarningAlert(false)}
          duration={5000}
        />

        {/* Journal Entry Popup */}
        {showJournalPopup && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          >
            <div 
              className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  {editingJournal ? 'Ерөнхий журналын бичилт засах' : 'Ерөнхий журналын бичилт нэмэх'}
                </h3>
                <button
                  onClick={() => {
                    setShowJournalPopup(false);
                    setEditingJournal(null);
                    setIsPreviousYear(false);
                    setJournalFormData({
                      Date: '',
                      Description: '',
                      AccountId: '',
                      OffsetAccountId: '',
                      AmountDebit: '',
                      AmountCredit: '',
                      VatAmount: '',
                      Amount: '',
                      CashflowCategoryOid: '',
                      YearType: '',
                      PeriodType: '2',
                      DayType: ''
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
                  disabled={loadingJournal}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Огноо <span className="text-red-500">*</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPreviousYear}
                        onChange={(e) => handlePreviousYearChange(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>Өмнөх он</span>
                    </label>
                  </div>
                  <input
                    type="date"
                    value={journalFormData.Date}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      const dateObj = new Date(newDate);
                      setJournalFormData({ 
                        ...journalFormData, 
                        Date: newDate,
                        YearType: dateObj.getFullYear().toString(),
                        PeriodType: (dateObj.getMonth() + 1).toString(),
                        DayType: dateObj.getDate().toString()
                      });
                      // Uncheck previous year if user manually changes date
                      if (isPreviousYear) {
                        setIsPreviousYear(false);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тайлбар <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={journalFormData.Description}
                    onChange={(e) => setJournalFormData({ ...journalFormData, Description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Тайлбар оруулах"
                    required
                  />
                </div>

             

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                   Дебит данс
                  </label>
                  <SearchableDropdown
                    options={accounts.map(account => ({
                      value: account.Oid,
                      label: `${account.Code} - ${account.Name}`
                    }))}
                    value={journalFormData.OffsetAccountId}
                    onChange={(value) => setJournalFormData({ ...journalFormData, OffsetAccountId: value })}
                    valueLabel={accounts.find(a => a.Oid === journalFormData.OffsetAccountId) 
                      ? `${accounts.find(a => a.Oid === journalFormData.OffsetAccountId)?.Code} - ${accounts.find(a => a.Oid === journalFormData.OffsetAccountId)?.Name}`
                      : ''}
                    placeholder="Дебит данс"
                    className="w-full"
                    enableAutoComplete={true}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                  Кредит данс<span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={accounts.map(account => ({
                      value: account.Oid,
                      label: `${account.Code} - ${account.Name}`
                    }))}
                    value={journalFormData.AccountId}
                    onChange={(value) => setJournalFormData({ ...journalFormData, AccountId: value })}
                    valueLabel={accounts.find(a => a.Oid === journalFormData.AccountId) 
                      ? `${accounts.find(a => a.Oid === journalFormData.AccountId)?.Code} - ${accounts.find(a => a.Oid === journalFormData.AccountId)?.Name}`
                      : ''}
                    placeholder="Кредит данс сонгох"
                    className="w-full"
                    enableAutoComplete={true}
                  />
                </div>

              

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дүн оруулах
                  </label>
                  <SmartAmountInput
                    value={journalFormData.AmountDebit}
                    onChange={(parsed) => {
                      const numericValue = parsed !== null ? parsed.toString() : '';
                      
                      // Calculate VatAmount = AmountDebit * 0.1 if checkbox is checked
                      let vatAmount = journalFormData.VatAmount;
                      if (isWithVat && numericValue) {
                        const amountDebitNum = parseFloat(numericValue);
                        const calculatedVat = amountDebitNum * 0.1;
                        vatAmount = calculatedVat > 0 ? calculatedVat.toString() : '';
                      }
                      
                      setJournalFormData({ 
                        ...journalFormData, 
                        AmountDebit: numericValue,
                        VatAmount: vatAmount
                      });
                    }}
                    onBlur={(parsed) => {
                      const numericValue = parsed !== null ? parsed.toString() : '';
                      
                      // Calculate VatAmount = AmountDebit * 0.1 if checkbox is checked
                      let vatAmount = journalFormData.VatAmount;
                      if (isWithVat && numericValue) {
                        const amountDebitNum = parseFloat(numericValue);
                        const calculatedVat = amountDebitNum * 0.1;
                        vatAmount = calculatedVat > 0 ? calculatedVat.toString() : '';
                      }
                      
                      setJournalFormData({ 
                        ...journalFormData, 
                        AmountDebit: numericValue,
                        VatAmount: vatAmount
                      });
                    }}
                    placeholder="Дүн оруулах"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={isWithVat}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsWithVat(checked);
                        if (!checked) {
                          // Clear VAT field when unchecked
                          setJournalFormData({
                            ...journalFormData,
                            VatAmount: ''
                          });
                        } else {
                          // Calculate VAT from AmountDebit when checked
                          if (journalFormData.AmountDebit) {
                            const amountDebitNum = parseFloat(journalFormData.AmountDebit);
                            const calculatedVat = amountDebitNum * 0.1;
                            setJournalFormData({
                              ...journalFormData,
                              VatAmount: calculatedVat > 0 ? calculatedVat.toString() : ''
                            });
                          }
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">НӨАТ-тэй</span>
                  </label>
                  {isWithVat && (
                    <SmartAmountInput
                      value={journalFormData.VatAmount}
                      onChange={(parsed) => {
                        const numericValue = parsed !== null ? parsed.toString() : '';
                        
                        // Calculate AmountDebit = VatAmount / 0.1 (or VatAmount * 10)
                        let amountDebit = journalFormData.AmountDebit;
                        if (numericValue) {
                          const vatAmountNum = parseFloat(numericValue);
                          const calculatedAmount = vatAmountNum / 0.1;
                          amountDebit = calculatedAmount > 0 ? calculatedAmount.toString() : '';
                        }
                        
                        setJournalFormData({ 
                          ...journalFormData, 
                          VatAmount: numericValue,
                          AmountDebit: amountDebit
                        });
                      }}
                      onBlur={(parsed) => {
                        const numericValue = parsed !== null ? parsed.toString() : '';
                        
                        // Calculate AmountDebit = VatAmount / 0.1 if checkbox is checked
                        let amountDebit = journalFormData.AmountDebit;
                        if (numericValue) {
                          const vatAmountNum = parseFloat(numericValue);
                          const calculatedAmount = vatAmountNum / 0.1;
                          amountDebit = calculatedAmount > 0 ? calculatedAmount.toString() : '';
                        }
                        
                        setJournalFormData({ 
                          ...journalFormData, 
                          VatAmount: numericValue,
                          AmountDebit: amountDebit
                        });
                      }}
                      placeholder="НӨАТ-ын дүн оруулах"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Мөнгөний урсгалын ангилал
                  </label>
                  <SearchableDropdown
                    options={cashflowCategories.map(category => ({
                      value: category.Oid,
                      label: `${category.Code} - ${category.Name}`
                    }))}
                    value={journalFormData.CashflowCategoryOid}
                    onChange={(value) => setJournalFormData({ ...journalFormData, CashflowCategoryOid: value })}
                    valueLabel={cashflowCategories.find(c => c.Oid === journalFormData.CashflowCategoryOid) 
                      ? `${cashflowCategories.find(c => c.Oid === journalFormData.CashflowCategoryOid)?.Code} - ${cashflowCategories.find(c => c.Oid === journalFormData.CashflowCategoryOid)?.Name}`
                      : ''}
                    placeholder="Мөнгөний урсгалын ангилал сонгох (Заавал биш)"
                    className="w-full"
                    enableAutoComplete={true}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowJournalPopup(false);
                    setEditingJournal(null);
                    setIsPreviousYear(false);
                    setJournalFormData({
                      Date: '',
                      Description: '',
                      AccountId: '',
                      OffsetAccountId: '',
                      AmountDebit: '',
                      AmountCredit: '',
                      VatAmount: '',
                      Amount: '',
                      CashflowCategoryOid: '',
                      YearType: '',
                      PeriodType: '2',
                      DayType: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={loadingJournal}
                >
                  Цуцлах
                </button>
                <button
                  onClick={handleSaveJournal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loadingJournal}
                >
                  {loadingJournal ? 'Хадгалж байна...' : (editingJournal ? 'Хадгалах' : 'Нэмэх')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Popup */}
        {showConfirmPopup && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]"
            style={{ zIndex: 9999 }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 relative z-[71]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-800 mb-4">Баталгаажуулах</h3>
              <p className="text-gray-700 mb-6">
                Та хаалтын гүйлгээг ledger-д оруулж, Trial Balance үүсгэхдээ итгэлтэй байна уу?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmPopup(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={loading}
                >
                  Үгүй
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('✅ Confirmation popup "Тийм" button clicked');
                    handleFinalSave();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                  type="button"
                >
                  {loading ? 'Ажиллаж байна...' : 'Тийм'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Undo Confirmation Popup */}
        {showUndoConfirmPopup && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]"
            style={{ zIndex: 9999 }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 relative z-[71]"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-800 mb-4">Баталгаажуулах</h3>
              <p className="text-gray-700 mb-6">
                Та энэ хаалтын гүйлгээг устгахдаа итгэлтэй байна уу?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowUndoConfirmPopup(false);
                    setUndoAccountPeriodCloseOid(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  disabled={loading}
                >
                  Үгүй
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUndoConfirm();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  disabled={loading}
                  type="button"
                >
                  {loading ? 'Ажиллаж байна...' : 'Тийм'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Popup Modal */}
        {showAddPopup && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
          >
            <div 
              className="bg-white rounded-lg shadow-xl p-6 max-w-[95vw] w-full mx-2 my-2 max-h-[95vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Хаалтын гүйлгээ нэмэх</h2>
                <button
                  onClick={handleAddCancel}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Step Indicator */}
              <div className="mb-6 flex items-center gap-2">
                <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    1
                  </div>
                  <span className="ml-2 text-sm font-medium">Бичилт хяналт</span>
                </div>
                <div className={`flex-1 h-1 ${currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    2
                  </div>
                  <span className="ml-2 text-sm font-medium">Ерөнхий журнал</span>
                </div>
                <div className={`flex-1 h-1 ${currentStep >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    3
                  </div>
                  <span className="ml-2 text-sm font-medium">НӨАТ хяналт</span>
                </div>
                <div className={`flex-1 h-1 ${currentStep >= 4 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${currentStep >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    4
                  </div>
                  <span className="ml-2 text-sm font-medium">Trial Balance</span>
                </div>
                <div className={`flex-1 h-1 ${currentStep >= 5 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${currentStep >= 5 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 5 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    5
                  </div>
                  <span className="ml-2 text-sm font-medium">Үр дүн</span>
                </div>
                <div className={`flex-1 h-1 ${currentStep >= 6 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${currentStep >= 6 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 6 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                    6
                  </div>
                  <span className="ml-2 text-sm font-medium">Сүүлийн байдал</span>
                </div>
              </div>

              {/* Step 1: Control Receipt */}
              {currentStep === 1 && (
                <Step1ControlReceipt
                  customers={customers}
                  newCloseData={newCloseData}
                  setNewCloseData={setNewCloseData}
                  controlReceiptData={controlReceiptData}
                  loadingControlReceipt={loadingControlReceipt}
                  syncingReceipts={syncingReceipts}
                  setSyncingReceipts={setSyncingReceipts}
                  fetchControlReceipt={fetchControlReceipt}
                  setErrorMessage={setErrorMessage}
                  setShowErrorPopup={setShowErrorPopup}
                  setWarningMessage={setWarningMessage}
                  setShowWarningAlert={setShowWarningAlert}
                  onCancel={handleAddCancel}
                  onContinue={handleControlReceiptContinue}
                  loading={loading}
                />
              )}

              {/* Step 2: General Journal */}
              {currentStep === 2 && (
                <Step2GeneralJournal
                  customers={customers}
                  newCloseData={newCloseData}
                  setNewCloseData={setNewCloseData}
                  noLedgerTransactionData={noLedgerTransactionData}
                  loadingNoLedgerTransaction={loadingNoLedgerTransaction}
                  rebuildingLedger={rebuildingLedger}
                  handleRebuildLedger={handleRebuildLedger}
                  ledgerRebuildError={ledgerRebuildError}
                  journalData={journalData}
                  loadingJournal={loadingJournal}
                  showJournalEntries={showJournalEntries}
                  setShowJournalEntries={setShowJournalEntries}
                  handleAddJournal={handleAddJournal}
                  handleEditJournal={handleEditJournal}
                  handleDeleteJournal={handleDeleteJournal}
                  onBack={handleBack}
                  onContinue={handleGeneralJournalContinue}
                  loading={loading}
                />
              )}

              {/* Step 3: VAT Control Report */}
              {currentStep === 3 && (
                <Step3VatReport
                  customers={customers}
                  newCloseData={newCloseData}
                  setNewCloseData={setNewCloseData}
                  vatReportData={vatReportData}
                  vatReportSummary={vatReportSummary}
                  loadingVatReport={loadingVatReport}
                  onBack={handleBack}
                  onContinue={handleVatReportContinue}
                  loading={loading}
                  onRefresh={() => {
                    if (newCloseData.CustomerOid && newCloseData.YearType) {
                      refreshVatReportFromTaxSystem(newCloseData.CustomerOid, newCloseData.YearType, newCloseData.PeriodType);
                    }
                  }}
                />
              )}

              {/* Step 4: Trial Balance */}
              {currentStep === 4 && (
                <Step4TrialBalance
                  loadingTrialBalance={loadingTrialBalance}
                  showTrialBalance={showTrialBalance}
                  trialBalanceData={trialBalanceData}
                  customers={customers}
                  newCloseData={newCloseData}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  loading={loading}
                  customerOid={selectedCustomerOid}
                  year={selectedYear}
                />
              )}

              {/* Step 5: AccountPeriodClose Result */}
              {currentStep === 5 && (
                <Step5Result
                  customers={customers}
                  newCloseData={newCloseData}
                  accountPeriodCloseResult={accountPeriodCloseResult}
                  accountPeriodCloseItems={accountPeriodCloseItems}
                  costOfGoodsSold={costOfGoodsSold}
                  setCostOfGoodsSold={setCostOfGoodsSold}
                  batch4Amount={batch4Amount}
                  loading={loading}
                  handleCalculate={handleCalculate}
                  onBack={handleBack}
                  onContinue={handleSaveClick}
                />
              )}

              {/* Step 6: Trial Balance Year */}
              {currentStep === 6 && (
                <Step6FinalStatus
                  loading={loading}
                  showTrialBalance={showTrialBalance}
                  trialBalanceData={trialBalanceData}
                  customers={customers}
                  newCloseData={newCloseData}
                  onBack={handleBack}
                  onSendToTax={handleSendToTax}
                  onFinish={() => {
                    // Close popup and refresh
                    setShowAddPopup(false);
                    setNewCloseData({
                      CustomerOid: '',
                      YearType: DEFAULT_YEAR_TYPE,
                      PeriodType: '2',
                      IsManual: '0',
                      ManualItemCostAmount: '0',
                      ClearOld: '1',
                      UseRandom: '1'
                    });
                    setTrialBalanceData([]);
                    setShowTrialBalance(false);
                    setVatReportData([]);
                    setCurrentStep(1);
                    setAccountPeriodCloseResult(null);
                    setAccountPeriodCloseItems([]);
                    setBatch4Amount(null);
                    setCostOfGoodsSold(null);
                    fetchAccountPeriodClose();
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default AccountPeriodClose;